import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Paper,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Check as CheckIcon,
  ShoppingCart as ShoppingCartIcon,
  Person,
  Business,
  Store as StoreIcon,
  LocationOn as LocationOnIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  MyLocation,
  CheckCircle,
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTimer } from '../contexts/TimerContext';
import { useAuth } from '../contexts/AuthContextNew';
import { useCart } from '../contexts/CartContext';
import EnhancedLocationDialog from '../components/EnhancedLocationDialog';
import BottomNavigation from '../components/BottomNavigation';
import { providerAPI, serviceBookingAPI, serviceAPI, cartAPI } from '../services/api';
import api from '../services/api';
import useResponsive from '../hooks/useResponsive';

// Add WebSocket service import
import websocketService from '../services/websocketService';

// Timer functionality temporarily removed - will add back after slot selection is fixed


// Fetch real available slots from backend API
const fetchAvailableSlots = async (providerId, date, services) => {
  const requestData = {
      providerId,
      date,
      services: services || []
  };
  
  try {
    console.log('🔍 [FETCH SLOTS] Request data:', requestData);
    
    const response = await api.post('/customer/availability', requestData);

    console.log('🔍 [FETCH SLOTS] Response:', response.data);

    if (response.data.success && response.data.data) {
      // Check if business is closed for this date
      if (response.data.data.isClosed) {
        console.log('🚫 [FETCH SLOTS] Business is closed:', response.data.data.message);
        return {
          isClosed: true,
          message: response.data.data.message,
          closedPeriod: response.data.data.closedPeriod,
          slots: []
        };
      }
      
      // New simplified response format - only availableSlots
      const availableSlots = response.data.data.availableSlots;
      
      if (availableSlots && availableSlots.length > 0) {
        console.log('✅ [FETCH SLOTS] Found available slots:', availableSlots.length);
        console.log('🔍 [FETCH SLOTS] First available slot:', availableSlots[0]);
        
        // Convert backend format to frontend format
        const formattedSlots = [{
          date: date,
          slots: availableSlots
        }];
        
        console.log('🔍 [FETCH SLOTS] Formatted slots:', formattedSlots);
        return formattedSlots;
      } else if (response.data.data.message) {
        console.log('⚠️ [FETCH SLOTS] No slots message:', response.data.data.message);
        return { 
          message: response.data.data.message, 
          slots: []
        };
    } else {
        console.log('⚠️ [FETCH SLOTS] No slots data');
        return { 
          message: 'No available slots', 
          slots: []
        };
      }
    } else {
      console.warn('⚠️ [FETCH SLOTS] No slots in response:', response.data);
      return { 
        message: 'No available slots', 
        slots: []
      };
    }
  } catch (error) {
    console.error('❌ [FETCH SLOTS] Error fetching real slots:', error);
    return { 
      message: 'Error fetching availability. Please try again.', 
      slots: []
    };
  }
};

const fetchCartItems = async () => {
  try {
    const response = await api.get('/customer/cart');
    return response.data.data?.items || [];
  } catch (error) {
    console.error('Error fetching cart items:', error);
      return [];
    }
};

const fetchTeamMembers = async (providerId) => {
  try {
    const response = await api.get(`/customer/providers/${providerId}/team-members`);
    return response.data.teamMembers || [];
  } catch (error) {
    console.error('Error fetching team members:', error);
    return [];
  }
};

// Team members will be loaded from API

// Utility functions removed - backend now handles all slot generation

const Availability = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { providerId } = useParams();
  const { timerState, startGlobalTimer, stopGlobalTimer, resetGlobalTimer } = useTimer();
  const { user } = useAuth();
  const { removeFromCart } = useCart();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  
  // Snackbar helper function
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  
  // Reschedule mode state
  const isReschedule = location.state?.isReschedule || false;
  const originalBooking = location.state?.originalBooking || null;
  const rescheduleFee = location.state?.rescheduleFee || 0;
  const rescheduleRule = location.state?.rescheduleRule || null;
  const [availableSlots, setAvailableSlots] = useState([]);
  // Initialize selectedDate - check localStorage first if timer is active
  const getInitialDate = () => {
    try {
      const savedDate = localStorage.getItem(`availability_${providerId}_selectedDate`);
      const savedTimerState = localStorage.getItem('globalTimerState');
      
      if (savedDate && savedTimerState) {
        const timerData = JSON.parse(savedTimerState);
        if (timerData.isVisible && timerData.expiresAt) {
          const expirationTime = new Date(timerData.expiresAt);
          const now = new Date();
          if (expirationTime > now) {
            console.log('🔄 [INIT] Restoring saved date (timer active):', savedDate);
            return savedDate; // Use saved date if timer is active
          }
        }
      }
    } catch (error) {
      console.error('Error checking initial date:', error);
    }
    
    // Get today's date in local timezone (not UTC)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    console.log('🔄 [INIT] Using today as initial date:', today);
    return today;
  };
  
  const [selectedDate, setSelectedDate] = useState(() => getInitialDate());
  
  // Initialize selectedTime with saved value if there's an active timer
  const getInitialTime = () => {
    try {
      const savedTime = localStorage.getItem(`availability_${providerId}_selectedTime`);
      const savedTimerState = localStorage.getItem('globalTimerState');
      
      if (savedTime && savedTimerState) {
        const timerData = JSON.parse(savedTimerState);
        if (timerData.isVisible && timerData.expiresAt) {
          const expirationTime = new Date(timerData.expiresAt);
          const now = new Date();
          if (expirationTime > now) {
            return savedTime; // Use saved time if timer is active
          }
        }
      }
    } catch (error) {
      console.error('Error checking initial time:', error);
    }
    return ''; // Default to empty
  };
  
  // Initialize selectedSlots with saved value if there's an active timer
  const getInitialSlots = () => {
    try {
      const savedSlots = localStorage.getItem(`availability_${providerId}_selectedSlots`);
      const savedTimerState = localStorage.getItem('globalTimerState');
      
      if (savedSlots && savedTimerState) {
        const timerData = JSON.parse(savedTimerState);
        if (timerData.isVisible && timerData.expiresAt) {
          const expirationTime = new Date(timerData.expiresAt);
          const now = new Date();
          if (expirationTime > now) {
            return JSON.parse(savedSlots); // Use saved slots if timer is active
          }
        }
      }
    } catch (error) {
      console.error('Error checking initial slots:', error);
    }
    return []; // Default to empty array
  };
  
  const [selectedTime, setSelectedTime] = useState(getInitialTime());
  const [selectedSlots, setSelectedSlots] = useState(getInitialSlots());
  
  // Initialize selectedTeamMember with saved value if there's an active timer
  const getInitialTeamMember = () => {
    try {
      const savedTeamMember = localStorage.getItem(`availability_${providerId}_selectedTeamMember`);
      const savedTimerState = localStorage.getItem('globalTimerState');
      
      if (savedTeamMember && savedTimerState) {
        const timerData = JSON.parse(savedTimerState);
        if (timerData.isVisible && timerData.expiresAt) {
          const expirationTime = new Date(timerData.expiresAt);
          const now = new Date();
          if (expirationTime > now) {
            return JSON.parse(savedTeamMember); // Use saved team member if timer is active
          }
        }
      }
    } catch (error) {
      console.error('Error checking initial team member:', error);
    }
    return null; // Default to null
  };
  
  const [selectedTeamMember, setSelectedTeamMember] = useState(getInitialTeamMember());
  const [loading, setLoading] = useState(true);
  const [partialLoading, setPartialLoading] = useState(false);
  const [slotSelecting, setSlotSelecting] = useState(false);
  const [error, setError] = useState(null);30
  const [noSlotsMessage, setNoSlotsMessage] = useState('');
  const [unavailableServices, setUnavailableServices] = useState([]);
  const [removingServices, setRemovingServices] = useState(false);
  
  // Snackbar state for user notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' // 'success', 'error', 'warning', 'info'
  });
  
  // Debug loading states
  console.log('🔍 [LOADING STATES]', {
    partialLoading,
    removingServices,
    loading
  });
  
  // Reset loading states on mount to prevent stuck states
  useEffect(() => {
    console.log('🔍 [RESET LOADING] Resetting loading states on mount');
    setPartialLoading(false);
    setRemovingServices(false);
  }, []);

  // New simple debug system
  const [debugInfo, setDebugInfo] = useState({
    lastRequest: null,
    lastResponse: null,
    error: null
  });

  // Function to copy all console logs
  const copyAllLogs = () => {
    // Get all console logs from the browser's console
    const logs = [];
    
    // Add current debug info
    logs.push('🔍 DEBUG INFO:');
    logs.push(`Last Request: ${JSON.stringify(debugInfo.lastRequest, null, 2)}`);
    logs.push(`Last Response: ${JSON.stringify(debugInfo.lastResponse, null, 2)}`);
    logs.push(`Error: ${debugInfo.error || 'None'}`);
    logs.push(`Available Slots Count: ${availableSlots.length}`);
    logs.push(`Selected Date: ${selectedDate}`);
    logs.push(`Cart Items: ${cartItems.length}`);
    logs.push('');
    
    // Add current state info
    logs.push('🔍 CURRENT STATE:');
    logs.push(`Selected Time: ${selectedTime}`);
    logs.push(`Selected Slots Count: ${selectedSlots.length}`);
    logs.push(`Current Reservation ID: ${currentReservationId}`);
    logs.push(`Current Reservation Keys: ${JSON.stringify(currentReservationKeys, null, 2)}`);
    logs.push(`Current Customer ID: ${currentCustomerId}`);
    logs.push(`Reservation Expires At: ${reservationExpiresAt}`);
    logs.push(`Timer State: ${JSON.stringify(timerState, null, 2)}`);
    logs.push('');
    
    // Add instructions for manual console log copying
    logs.push('📋 TO COPY CONSOLE LOGS:');
    logs.push('1. Open Browser Developer Tools (F12)');
    logs.push('2. Go to Console tab');
    logs.push('3. Right-click in console and select "Save as..." or copy manually');
    logs.push('4. Or use browser console export feature');
    
    const logText = logs.join('\n');
    
    // Try to copy to clipboard, but if it fails, show the text in a prompt
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(logText).then(() => {
        }).catch(() => {
          // Fallback: show text in prompt for manual copying
          prompt('Copy this debug info (Ctrl+A, Ctrl+C):', logText);
        });
      } else {
        // Fallback: show text in prompt for manual copying
        prompt('Copy this debug info (Ctrl+A, Ctrl+C):', logText);
      }
    } catch (error) {
      // Fallback: show text in prompt for manual copying
      prompt('Copy this debug info (Ctrl+A, Ctrl+C):', logText);
    }
  };

  const [provider, setProvider] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [serviceAddress, setServiceAddress] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [availableTeamMembers, setAvailableTeamMembers] = useState([]);

  // Location dialog state
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [slotsIntentionallyReleased, setSlotsIntentionallyReleased] = useState(false);
  
  // Timer position state for draggable functionality - positioned on right side above cart
  const [timerPosition, setTimerPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      return { 
        x: Math.max(0, window.innerWidth - 100), // Right side
        y: Math.max(0, window.innerHeight - 160) // Above cart position
      };
    }
    return { x: 0, y: 0 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);

  // Timer drag handlers
  const handleTimerMouseDown = (e) => {
    if (e.target.closest('button')) return; // Don't drag if clicking on buttons
    
    setIsDragging(true);
    const rect = timerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleTimerMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Keep timer within viewport bounds - prefer right side positioning
    const maxX = window.innerWidth - 60; // Account for smaller timer size
    const maxY = window.innerHeight - 160; // Keep above cart area
    const minX = window.innerWidth - 120; // Prefer right side
    const minY = 10; // Minimum top margin
    
    setTimerPosition({
      x: Math.max(minX, Math.min(newX, maxX)),
      y: Math.max(minY, Math.min(newY, maxY))
    });
  };

  const handleTimerMouseUp = () => {
    setIsDragging(false);
  };

  // Add drag event listeners
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleTimerMouseMove);
      document.addEventListener('mouseup', handleTimerMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleTimerMouseMove);
        document.removeEventListener('mouseup', handleTimerMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Handle window resize for timer positioning
  React.useEffect(() => {
    const handleResize = () => {
      if (timerState.isVisible) {
        setTimerPosition(prevPosition => ({
          x: Math.max(window.innerWidth - 120, Math.min(prevPosition.x, window.innerWidth - 60)),
          y: Math.max(0, Math.min(prevPosition.y, window.innerHeight - 160))
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [timerState.isVisible]);

  // Removed problematic useEffect that was stopping timer when selectedSlots changed
  

  // Get cart items from API or location state
  const getCartItems = async (id) => {
    if (location.state?.cartItems) {
      return location.state.cartItems;
    }
    
    if (location.state?.items) {
      return location.state.items;
    }
    
    try {
      // Get provider services from API
      const response = await providerAPI.getProviderServices(id);
      if (response.success && response.data.length > 0) {
        // Convert API response to cart items format
        return response.data.map(service => ({
          id: service._id,
          name: service.serviceName,
          price: service.price,
          duration: service.duration || 60,
          image: service.serviceImage || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop'
        }));
      } else {
       // console.error('Failed to fetch provider services:', response.error);
        return [];
      }
    } catch (error) {
    //  console.error('Error fetching provider services:', error);
              return [];
    }
  };

  // Filter slots to show only those after current time for today
  const filterSlotsByCurrentTime = (slots, date) => {
    // Get today's date in local timezone (not UTC)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    const isToday = date === today;
    
    if (!isToday) {
    //  console.log('🔍 [FRONTEND] Not today - returning all slots for future date:', date);
      return slots; // Return all slots for future dates
    }
    
    // Use the existing 'now' variable instead of redeclaring
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const nextIntervalMinutes = Math.ceil(currentMinutes / 10) * 10;
    const nextIntervalTime = `${Math.floor(nextIntervalMinutes / 60).toString().padStart(2, '0')}:${(nextIntervalMinutes % 60).toString().padStart(2, '0')}`;
    
   // console.log('🔍 [FRONTEND] Filtering slots for today - current time:', `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`);
   // console.log('🔍 [FRONTEND] Next 10-minute interval:', nextIntervalTime);
    
    const filteredSlots = slots.filter(slot => {
      const slotTime = slot.startTime;
      const isAfterNextInterval = slotTime >= nextIntervalTime;
   //   console.log(`🔍 [FRONTEND] Slot ${slotTime} vs next interval ${nextIntervalTime}: ${isAfterNextInterval ? 'KEEP' : 'FILTER OUT'}`);
      return isAfterNextInterval;
    });
    
   // console.log(`🔍 [FRONTEND] Filtered ${slots.length} slots to ${filteredSlots.length} slots for today`);
    return filteredSlots;
  };

  // Fetch provider-specific availability slots from API
  const fetchProviderAvailability = async (providerId, services = [], date = null) => {
    try {
  //    console.log('🔍 Fetching real availability for provider:', providerId);
  //    console.log('🔍 Services:', services);
  //    console.log('🔍 Date:', date || selectedDate);
      
      // Convert service IDs to full service objects if needed
      const serviceObjects = services.map(serviceId => {
        if (typeof serviceId === 'string') {
          // If it's just an ID, find the full service object from cartItems
          const cartItem = cartItems.find(item => item.id === serviceId);
          return cartItem ? {
            id: cartItem.id,
            name: cartItem.name,
            duration: cartItem.duration,
            price: cartItem.price,
            quantity: cartItem.quantity || 1
          } : { id: serviceId };
        }
        return serviceId;
      });
      
      // Debug: Log what we're sending to fetchAvailableSlots
   //   console.log('🔍 [LOAD SLOTS] Calling fetchAvailableSlots with:', {
   //     providerId,
   //     date: date || selectedDate,
   //     serviceObjects,
   //     cartItems: cartItems
   //   });
      
      // Use the real backend API with provider ID
      const response = await fetchAvailableSlots(providerId, date || selectedDate, serviceObjects);
      
      // Update debug info
      setDebugInfo({
        lastRequest: { providerId, date: date || selectedDate, services: serviceObjects },
        lastResponse: response,
        error: null
      });
      
      // Check if business is closed for this date
      if (response && response.isClosed) {
        console.log('🚫 [FRONTEND] Business is closed:', response.message);
        let closedMessage = response.message || '';
        // Remove time information from closed message
        closedMessage = closedMessage.replace(/\s*\(current time:\s*\d{1,2}:\d{2}\)\s*/gi, '');
        setAvailableSlots([]);
        setNoSlotsMessage(closedMessage);
        setUnavailableServices([]);
        return [];
      }
      
      // Check if response contains a message (no slots available)
      if (response && (response.message || (response.data && response.data.message))) {
        let message = response.data?.message || response.message;
        const unavailableServices = response.data?.unavailableServices || response.unavailableServices || [];
        
        // Remove time information from message (e.g., "(current time: 17:01)")
        message = message.replace(/\s*\(current time:\s*\d{1,2}:\d{2}\)\s*/gi, '');
        
        // Check if this is for today's date and customize message
        const today = new Date().toISOString().split('T')[0];
        const isToday = date === today;
        const isNoSlotsMessage = message.toLowerCase().includes('no slots') || 
                                 message.toLowerCase().includes('no available') ||
                                 message.toLowerCase().includes('not available');
        
        if (isToday && isNoSlotsMessage) {
          message = 'Fully booked for today';
        }
        
        setAvailableSlots([]);
        setNoSlotsMessage(message);
        setUnavailableServices(unavailableServices);
        return [];
      }
      
      if (response && response.length > 0) {
   //     console.log('✅ Available slots fetched successfully:', response);
        setNoSlotsMessage(''); // Clear any previous no slots message
        setUnavailableServices([]); // Clear any previous unavailable services
        
        // Response is already formatted by fetchProviderAvailability
        // Extract the slots from the formatted response
        const dayData = response[0]; // Get the first (and only) day
        const rawSlots = dayData.slots; // Get the actual slots array
        
        // Convert backend available slots to frontend format
        const slots = rawSlots.map(slot => ({
              startTime: slot.startTime,
              endTime: slot.endTime,
          available: true, // All slots from backend are available
          isSelected: false,
          duration: slot.duration,
          schedule: slot.schedule,
          staffAllocation: slot.staffAllocation,
          serviceColor: slot.serviceColor,
          isProService: slot.isProService,
          bufferTime: slot.bufferTime,
          providerType: slot.providerType,
          businessRules: slot.businessRules,
          // Complete schedule information
          completeSchedule: slot.completeSchedule,
          totalDuration: slot.totalDuration
        }));
        
        // Filter slots by current time for today
        const dateKey = date || selectedDate;
        const filteredSlots = filterSlotsByCurrentTime(slots, dateKey);
        
        // Group slots by date
        const daySlots = {
          date: dateKey,
          dayName: new Date(dateKey).toLocaleDateString('en-US', { weekday: 'short' }),
          slots: filteredSlots
        };
        
   //     console.log('✅ Processed available slots:', [daySlots]);
   //     console.log('🔍 [LOAD SLOTS] First slot schedule:', daySlots.slots[0]?.schedule);
        return [daySlots];
            } else {
   //     console.warn('⚠️ No available slots for this date');
        return [];
      }
    } catch (error) {
    //  console.error('❌ Error fetching real availability:', error);
      
      // Update debug info with error
      setDebugInfo(prev => ({
        ...prev,
        error: error.message || 'Unknown error'
      }));
      
      return [];
    }
  };

  // Function to check if a slot has enough consecutive availability
  const hasConsecutiveAvailability = (daySlots, startIndex, requiredSlots) => {
    for (let i = 0; i < requiredSlots; i++) {
      const slot = daySlots.slots[startIndex + i];
      if (!slot || !slot.available) {
        return false;
      }
    }
    return true;
  };

  // Function to get available slots with consecutive availability (considering team members)
  const getAvailableSlotsWithConsecutiveAvailability = async (daySlots, requiredSlots) => {
    if (!daySlots || !daySlots.slots) return [];
    
    console.log(`🔍 Checking availability for ${requiredSlots} consecutive slots on ${daySlots.date}`);
    
    // If no team members are available for the services, return empty array
    const availableMembers = await getAvailableTeamMembers(providerId, cartItems);
 //   console.log('🔍 Available team members for services:', availableMembers.map(m => m.name));
    
    if (availableMembers.length === 0) {
  //    console.log('❌ No team members available for services');
      return [];
    }
    
    const slotsWithTeamAvailability = getAvailableSlotsWithTeamMemberAvailability(daySlots, requiredSlots);
//    console.log(`✅ Found ${slotsWithTeamAvailability.length} slots with 100% availability and team member compliance`);
    
    return slotsWithTeamAvailability;
  };


  // Function to get team members who can perform the selected services
  const getAvailableTeamMembers = async (providerId, cartItems) => {
    const allTeamMembers = await fetchTeamMembers(providerId);
 //   console.log('🔍 All team members for provider:', allTeamMembers.map(m => `${m.name} (${m.services.join(', ')})`));
    
    // Get all service IDs from cart
    const requiredServiceIds = cartItems.map(item => item.id);
  //  console.log('🔍 Required service IDs from cart:', requiredServiceIds);
    
    // Filter team members who can perform ANY of the required services
    // (For sequential booking, we need team members who can handle different services)
    const availableMembers = allTeamMembers.filter(member => {
      const canPerform = requiredServiceIds.some(serviceId => 
        member.services.includes(serviceId)
      );
      
      if (canPerform) {
        const memberServices = requiredServiceIds.filter(serviceId => 
          member.services.includes(serviceId)
        );
   //     console.log(`✅ ${member.name} can perform:`, memberServices.map(id => {
   //       const service = cartItems.find(item => item.id === id);
   //       return `${service?.name} (${service?.duration}min)`;
   //     }));
      } else {
   //     console.log(`❌ ${member.name} cannot perform any required services`);
      }
      
      return canPerform;
    });
    
  //  console.log(`🎯 Total available team members: ${availableMembers.length}`);
    return availableMembers;
  };

  // Function to automatically select the best available team member for a time slot
  const getBestAvailableTeamMember = (timeSlot) => {
    if (!selectedDate || !timeSlot) return null;
    
    const availableMembers = getAvailableTeamMembers(providerId, cartItems);
    const totalDuration = cartItems.reduce((total, item) => {
      let duration = item.duration || 60;
      // Ensure duration is a number
      if (typeof duration === 'string') {
        duration = parseInt(duration.replace(/[^0-9]/g, '')) || 60;
      } else {
        duration = parseInt(duration) || 60;
      }
      return total + duration;
    }, 0);
    const requiredSlots = Math.ceil(totalDuration / 10); // 10 minutes per slot
    
    // Find team members who are available for the entire consecutive slot range
    const availableForSlot = availableMembers.filter(member => 
      isTeamMemberAvailableForConsecutiveSlots(member, selectedDate, timeSlot, requiredSlots)
    );
    
    if (availableForSlot.length === 0) return null;
    
    // For now, return the first available member
    // In a real system, you might have more sophisticated logic (rating, experience, etc.)
    return availableForSlot[0];
  };

  // Function to check if a team member is available on a specific date and time
  const isTeamMemberAvailable = (teamMember, date, timeSlot) => {
    const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'lowercase' });
    const shift = teamMember.shifts[dayOfWeek];
    
    if (!shift) return false;
    
    const slotTime = timeSlot.startTime;
    const shiftStart = shift.start;
    const shiftEnd = shift.end;
    
    return slotTime >= shiftStart && slotTime < shiftEnd;
  };

  // Function to check if a team member is available for consecutive slots (considering shift end time and existing bookings)
  const isTeamMemberAvailableForConsecutiveSlots = (teamMember, date, startSlot, requiredSlots) => {
    const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const shift = teamMember.shifts[dayOfWeek];
    
    if (!shift) {
      console.log(`❌ ${teamMember.name} has no shift on ${dayOfWeek}`);
      return false;
    }
    
    const shiftStart = shift.start;
    const shiftEnd = shift.end;
    
    // Check if the first slot is within shift
    if (startSlot.startTime < shiftStart) {
      console.log(`❌ ${teamMember.name}: Slot ${startSlot.startTime} is before shift start ${shiftStart}`);
      return false;
    }
    
    // Calculate the end time of the last slot
    const totalDurationMinutes = requiredSlots * 10; // 10 minutes per slot
    const startTime = new Date(`2000-01-01T${startSlot.startTime}:00`);
    const endTime = new Date(startTime.getTime() + totalDurationMinutes * 60000);
    const lastSlotEndTime = endTime.toTimeString().slice(0, 5); // Format as HH:MM
    
    // Check if the entire service duration fits within the shift
    if (lastSlotEndTime > shiftEnd) {
      console.log(`❌ ${teamMember.name}: Service ends at ${lastSlotEndTime} but shift ends at ${shiftEnd}`);
      return false;
    }
    
    // Check for existing bookings (simulated)
    const existingBookings = getExistingBookings(teamMember.id, date);
    for (const booking of existingBookings) {
      if (hasTimeOverlap(startSlot.startTime, lastSlotEndTime, booking.startTime, booking.endTime)) {
        console.log(`❌ ${teamMember.name}: Conflicts with existing booking ${booking.startTime}-${booking.endTime}`);
        return false;
      }
    }
    
    console.log(`✅ ${teamMember.name}: Available for ${startSlot.startTime}-${lastSlotEndTime} (shift: ${shiftStart}-${shiftEnd})`);
    return true;
  };

  // Function to get existing bookings for a team member (simulated)
  const getExistingBookings = (memberId, date) => {
    // Simulate existing bookings based on your scenario
    const bookings = {
      'tm-1': [ // Alice
        { startTime: '10:00', endTime: '11:00' }
      ],
      'tm-2': [ // John
        { startTime: '10:00', endTime: '11:30' },
        { startTime: '12:30', endTime: '13:30' }
      ]
    };
    
    return bookings[memberId] || [];
  };

  // Function to check if two time ranges overlap
  const hasTimeOverlap = (start1, end1, start2, end2) => {
    return start1 < end2 && start2 < end1;
  };

  // Function to get available slots considering team member availability (sequential booking)
  const getAvailableSlotsWithTeamMemberAvailability = (daySlots, requiredSlots) => {
    if (!daySlots || !daySlots.slots) return [];
    
    const availableSlots = [];
    
    for (let i = 0; i <= daySlots.slots.length - requiredSlots; i++) {
      // Check if ALL consecutive slots are 100% available (no gaps)
      let slotsAvailable = true;
      for (let j = 0; j < requiredSlots; j++) {
        const slot = daySlots.slots[i + j];
        if (!slot || !slot.available) {
          slotsAvailable = false;
          break;
        }
      }
      
      if (slotsAvailable) {
        // Check if services can be completed sequentially by available team members
        const firstSlot = daySlots.slots[i];
        // Note: This is a synchronous function calling an async function
        // For now, we'll assume slots are available if they pass the basic checks
        // In a real implementation, you'd need to make this function async and await the result
          availableSlots.push(daySlots.slots[i]);
      }
    }
    
    console.log(`🔍 Total slots checked: ${daySlots.slots.length - requiredSlots + 1}`);
    console.log(`🔍 Slots with 100% consecutive availability: ${availableSlots.length}`);
    
    return availableSlots;
  };

  // Function to check if services can be completed sequentially with shift time validation
  const canCompleteServicesSequentially = async (startSlot, totalRequiredSlots) => {
    const availableMembers = await getAvailableTeamMembers(providerId, cartItems);
    if (availableMembers.length === 0) {
      console.log('❌ No team members available for services');
      return false;
    }

    console.log(`🔍 Checking sequential completion starting at ${startSlot.startTime}`);
    
    // Get service assignments for each team member
    const serviceAssignments = getServiceAssignments(availableMembers);
    
    // Check if all services can be completed sequentially
    const daySlots = availableSlots.find(day => day.date === selectedDate);
    if (!daySlots) {
      console.log('❌ No day slots found for selected date');
      return false;
    }
    
    let currentSlotIndex = daySlots.slots.findIndex(slot => slot.startTime === startSlot.startTime);
    let remainingSlots = totalRequiredSlots;
    
    console.log(`📅 Starting from slot index ${currentSlotIndex} (${startSlot.startTime})`);
    console.log(`⏱️ Total required slots: ${totalRequiredSlots}`);
    
    for (const assignment of serviceAssignments) {
      const { member, services, totalDuration } = assignment;
      const requiredSlotsForServices = Math.ceil(totalDuration / 10);
      
      console.log(`👤 Checking ${member.name} for services:`, services.map(id => {
        const service = cartItems.find(item => item.id === id);
        return `${service?.name} (${service?.duration}min)`;
      }), `- ${requiredSlotsForServices} slots needed`);
      
      // Check if this member can complete their services starting from current slot
      if (!canMemberCompleteServices(member, currentSlotIndex, requiredSlotsForServices)) {
        console.log(`❌ ${member.name} cannot complete their services starting from slot ${currentSlotIndex}`);
        return false;
      }
      
      console.log(`✅ ${member.name} can complete their services from slot ${currentSlotIndex} to ${currentSlotIndex + requiredSlotsForServices - 1}`);
      
      // Move to next slot after this member's services
      currentSlotIndex += requiredSlotsForServices;
      remainingSlots -= requiredSlotsForServices;
    }
    
    const canComplete = remainingSlots === 0;
    console.log(`🎯 Sequential completion result: ${canComplete ? '✅ SUCCESS' : '❌ FAILED'}`);
    
    return canComplete;
  };

  // Function to get service assignments for team members with shift time consideration
  const getServiceAssignments = (availableMembers) => {
    const assignments = [];
    const serviceIds = cartItems.map(item => item.id);
    
    console.log('🔍 Service Assignment Process:');
    console.log('Required services:', serviceIds.map(id => {
      const service = cartItems.find(item => item.id === id);
      return `${service?.name} (${service?.duration}min)`;
    }));
    
    // Create a map of which services each member can do
    const memberServiceMap = {};
    availableMembers.forEach(member => {
      const memberServices = member.services.filter(serviceId => 
        serviceIds.includes(serviceId)
      );
      memberServiceMap[member.id] = memberServices;
      
      console.log(`👤 ${member.name} can do:`, memberServices.map(id => {
        const service = cartItems.find(item => item.id === id);
        return `${service?.name} (${service?.duration}min)`;
      }));
    });
    
    // Assign services to members considering shift times
    const assignedServices = new Set();
    
    // First pass: assign services that only one member can do
    availableMembers.forEach(member => {
      const memberServices = memberServiceMap[member.id];
      const unassignedServices = memberServices.filter(serviceId => !assignedServices.has(serviceId));
      
      if (unassignedServices.length > 0) {
        const totalDuration = unassignedServices.reduce((total, serviceId) => {
          const service = cartItems.find(item => item.id === serviceId);
          let duration = service?.duration || 60;
          // Ensure duration is a number
          if (typeof duration === 'string') {
            duration = parseInt(duration.replace(/[^0-9]/g, '')) || 60;
          } else {
            duration = parseInt(duration) || 60;
          }
          return total + duration;
        }, 0);
        
        assignments.push({
          member,
          services: unassignedServices,
          totalDuration
        });
        
        unassignedServices.forEach(serviceId => assignedServices.add(serviceId));
        
        console.log(`✅ Assigned to ${member.name}:`, unassignedServices.map(id => {
          const service = cartItems.find(item => item.id === id);
          return `${service?.name} (${service?.duration}min)`;
        }), `Total: ${totalDuration}min`);
      }
    });
    
    // Second pass: assign remaining services to available members
    const remainingServices = serviceIds.filter(serviceId => !assignedServices.has(serviceId));
    if (remainingServices.length > 0) {
      console.log('🔄 Remaining services to assign:', remainingServices.map(id => {
        const service = cartItems.find(item => item.id === id);
        return `${service?.name} (${service?.duration}min)`;
      }));
      
      // Find members who can do remaining services
      remainingServices.forEach(serviceId => {
        const availableMembersForService = availableMembers.filter(member => 
          member.services.includes(serviceId)
        );
        
        if (availableMembersForService.length > 0) {
          // Assign to the first available member (could be optimized)
          const assignedMember = availableMembersForService[0];
          const service = cartItems.find(item => item.id === serviceId);
          
          // Check if member already has assignments
          const existingAssignment = assignments.find(a => a.member.id === assignedMember.id);
          if (existingAssignment) {
            existingAssignment.services.push(serviceId);
            existingAssignment.totalDuration += service?.duration || 60;
            console.log(`➕ Added ${service?.name} to ${assignedMember.name}'s existing assignment`);
          } else {
            assignments.push({
              member: assignedMember,
              services: [serviceId],
              totalDuration: service?.duration || 60
            });
            console.log(`✅ Assigned ${service?.name} to ${assignedMember.name}`);
          }
          
          assignedServices.add(serviceId);
        }
      });
    }
    
    // Check if all services are assigned
    const allAssigned = serviceIds.every(serviceId => assignedServices.has(serviceId));
    if (!allAssigned) {
      console.log('❌ Warning: Not all services could be assigned to team members');
      console.log('Required services:', serviceIds);
      console.log('Assigned services:', Array.from(assignedServices));
    } else {
      console.log('✅ All services successfully assigned to team members');
    }
    
    return assignments;
  };

  // Function to check if a member can complete their assigned services within shift times
  const canMemberCompleteServices = (member, startSlotIndex, requiredSlots) => {
    const daySlots = availableSlots.find(day => day.date === selectedDate);
    if (!daySlots) {
      console.log(`❌ No day slots found for ${member.name}`);
      return false;
    }
    
    // Get the start slot
    const startSlot = daySlots.slots[startSlotIndex];
    if (!startSlot) {
      console.log(`❌ Start slot not found for ${member.name} at index ${startSlotIndex}`);
      return false;
    }
    
    // Get the end slot
    const endSlotIndex = startSlotIndex + requiredSlots - 1;
    const endSlot = daySlots.slots[endSlotIndex];
    if (!endSlot) {
      console.log(`❌ End slot not found for ${member.name} at index ${endSlotIndex}`);
      return false;
    }
    
    console.log(`🔍 Checking ${member.name} from ${startSlot.startTime} to ${endSlot.endTime} (${requiredSlots} slots)`);
    
    // Check if the entire service duration fits within member's shift
    const dayOfWeek = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const shift = member.shifts[dayOfWeek];
    
    if (!shift) {
      console.log(`❌ ${member.name} has no shift on ${dayOfWeek}`);
      return false;
    }
    
    const shiftStart = shift.start;
    const shiftEnd = shift.end;
    
    // Check if start time is within shift
    if (startSlot.startTime < shiftStart) {
      console.log(`❌ ${member.name}: Service starts at ${startSlot.startTime} but shift starts at ${shiftStart}`);
      return false;
    }
    
    // Check if end time is within shift
    if (endSlot.endTime > shiftEnd) {
      console.log(`❌ ${member.name}: Service ends at ${endSlot.endTime} but shift ends at ${shiftEnd}`);
      return false;
    }
    
    // Check for existing booking conflicts
    const existingBookings = getExistingBookings(member.id, selectedDate);
    for (const booking of existingBookings) {
      if (hasTimeOverlap(startSlot.startTime, endSlot.endTime, booking.startTime, booking.endTime)) {
        console.log(`❌ ${member.name}: Conflicts with existing booking ${booking.startTime}-${booking.endTime}`);
        return false;
      }
    }
    
    // Check if all slots in between are available
    for (let i = 0; i < requiredSlots; i++) {
      const slot = daySlots.slots[startSlotIndex + i];
      if (!slot || !slot.available) {
        console.log(`❌ ${member.name}: Slot ${slot?.startTime} is not available`);
        return false;
      }
    }
    
    console.log(`✅ ${member.name}: Can complete services from ${startSlot.startTime} to ${endSlot.endTime} (shift: ${shiftStart}-${shiftEnd})`);
    return true;
  };

  // Get current location for service address
  const getCurrentLocation = async () => {
    setAddressLoading(true);
    try {
      if (!navigator.geolocation) {
        setServiceAddress('Location services not available');
        return;
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true
        });
      });

      const { latitude, longitude } = position.coords;
      
      // In a real app, you would reverse geocode these coordinates
      // For now, we'll use a mock address without coordinates
      const mockAddress = `123 Main Street, Apt 4B, New York, NY 10001`;
      setServiceAddress(mockAddress);
    } catch (error) {
      console.error('Error getting location:', error);
      setServiceAddress('Unable to get current location');
    } finally {
      setAddressLoading(false);
    }
  };

  // Handle address change
  const handleAddressChange = (newAddress) => {
    setServiceAddress(newAddress);
    setShowAddressDialog(false);
  };

  // Open location dialog for address selection
  const handleLocationSelect = () => {
    setLocationDialogOpen(true);
  };

  // State for slot reservation
  const [currentReservationId, setCurrentReservationId] = useState(null);
  const [currentReservationKeys, setCurrentReservationKeys] = useState([]);
  const [reservationExpiresAt, setReservationExpiresAt] = useState(null);
  const [currentCustomerId, setCurrentCustomerId] = useState(null);
  const [currentStaffAllocation, setCurrentStaffAllocation] = useState(null);
  
  // Use refs to persist reservation data across renders
  const currentReservationIdRef = useRef(null);
  const currentReservationKeysRef = useRef([]);
  const currentCustomerIdRef = useRef(null);
  const slotSelectingRef = useRef(false); // Use ref for synchronous checking to prevent race conditions
  const currentStaffAllocationRef = useRef(null);
  
  
  

  // Cache for availability data to speed up repeated requests
  const [availabilityCache, setAvailabilityCache] = useState(new Map());
  const [cacheTimestamps, setCacheTimestamps] = useState(new Map());
  

  // Save selected slots state to localStorage
  const saveSelectedSlotsState = (slots, time, teamMember) => {
    localStorage.setItem(`availability_${providerId}_selectedSlots`, JSON.stringify(slots));
    localStorage.setItem(`availability_${providerId}_selectedTime`, time);
    localStorage.setItem(`availability_${providerId}_selectedTeamMember`, JSON.stringify(teamMember));
    localStorage.setItem(`availability_${providerId}_selectedDate`, selectedDate);
  };

  // Generate comprehensive logs for debugging
  const generateDebugReport = () => {
    const report = {
      timestamp: new Date().toISOString(),
      page: 'Availability',
      user: {
        isAuthenticated: !!user,
        userId: user?.id,
        userEmail: user?.email
      },
      provider: {
        id: providerId,
        name: provider?.name,
        type: provider?.type,
        providerType: provider?.providerType,
        address: provider?.address,
        street: provider?.street,
        city: provider?.city,
        state: provider?.state,
        zipCode: provider?.zipCode,
        country: provider?.country,
        fullAddress: provider?.fullAddress
      },
      selectedDate,
      cartItems: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        duration: item.duration,
        price: item.price
      })),
      state: {
        selectedTime,
        selectedSlots: selectedSlots.map(s => s.startTime),
        selectedTeamMember: selectedTeamMember,
        currentReservationId,
        currentCustomerId,
        timerState: {
          isVisible: timerState.isVisible,
          seconds: timerState.seconds,
          title: timerState.title
        }
      },
      availableSlots: availableSlots.map(day => ({
        date: day.date,
        slotsCount: day.slots?.length || 0,
        slots: day.slots?.map(s => s.startTime) || []
      })),
      logs: allLogs
    };
    
    return JSON.stringify(report, null, 2);
  };



  // Load selected slots state from localStorage
  const loadSelectedSlotsState = () => {
    // Don't load state if slots were intentionally released
    if (slotsIntentionallyReleased) {
      return;
    }
    
    try {
      const savedDate = localStorage.getItem(`availability_${providerId}_selectedDate`);
      
      // Check if there's a running timer - if so, restore slots
      // Use timerState from context instead of localStorage for accuracy
      let shouldRestoreSlots = false;
      
      console.log('🔄 [STATE] Checking timer state for slot restoration', {
        timerStateFromContext: timerState,
        providerId: providerId
      });
      
      if (timerState.isVisible && timerState.expiresAt) {
        const expirationTime = new Date(timerState.expiresAt);
        const now = new Date();
        shouldRestoreSlots = expirationTime > now;
        
        console.log('🔄 [STATE] Timer expiration check from context', {
          expirationTime: expirationTime.toISOString(),
          now: now.toISOString(),
          shouldRestoreSlots: shouldRestoreSlots,
          timerVisible: timerState.isVisible
        });
      }
      
      if (savedDate) {
        setSelectedDate(savedDate);
        console.log('🔄 [STATE] Restored selected date', { savedDate });
      }
      
      // Always try to restore slots if there's a saved date (indicates previous selection)
      // Only clear slots if there's no saved date AND no active timer
      if (savedDate || shouldRestoreSlots) {
        // Restore slot selections if there's a saved date or timer is running
        const savedSlots = localStorage.getItem(`availability_${providerId}_selectedSlots`);
        const savedTime = localStorage.getItem(`availability_${providerId}_selectedTime`);
        const savedTeamMember = localStorage.getItem(`availability_${providerId}_selectedTeamMember`);
        
        
        if (savedSlots) {
          const slots = JSON.parse(savedSlots);
          setSelectedSlots(slots);
          console.log('🔄 [STATE] Restored selected slots from localStorage', {
            slots: slots,
            providerId: providerId
          });
        }
        
        if (savedTime) {
          setSelectedTime(savedTime);
          console.log('🔄 [STATE] Restored selected time', { savedTime });
        }
        
        if (savedTeamMember) {
          setSelectedTeamMember(savedTeamMember);
          console.log('🔄 [STATE] Restored selected team member', { savedTeamMember });
        }
      } else {
      // Clear any saved slot selections to ensure fresh state
        console.log('🔄 [STATE] Not restoring slots - clearing saved state', {
          shouldRestoreSlots: shouldRestoreSlots,
          savedDate: savedDate,
          providerId: providerId
        });
      localStorage.removeItem(`availability_${providerId}_selectedSlots`);
      localStorage.removeItem(`availability_${providerId}_selectedTime`);
      localStorage.removeItem(`availability_${providerId}_selectedTeamMember`);
      }
      
      console.log('🔄 [STATE] Loaded selected date from localStorage', {
        savedDate: savedDate,
        providerId: providerId,
        shouldRestoreSlots: shouldRestoreSlots
      });
    } catch (error) {
      console.error('Error loading selected slots state:', error);
    }
  };

  // Refresh availability function
  const refreshAvailability = async () => {
    try {
      console.log('Starting availability refresh', { 
        providerId, 
        selectedDate, 
        serviceCount: cartItems.length 
      });
      
      const serviceIds = cartItems.map(item => item.id);
      const updatedSlots = await fetchProviderAvailability(providerId, serviceIds, selectedDate);
      
      console.log('Availability refresh completed', { 
        slotsCount: updatedSlots.length,
        firstFewSlots: updatedSlots.slice(0, 5).map(slot => slot.time)
      });
      
      setAvailableSlots(updatedSlots);
    } catch (error) {
      console.error('Error refreshing availability:', error);
      console.log('Availability refresh failed', { error: error.message });
    }
  };

  // Reserve slots in backend when user selects them
  const reserveSelectedSlots = async (slots, time, teamMember) => {
    try {
      console.log('reserveSelectedSlots called', { 
        slotsCount: slots.length, 
        time, 
        teamMember: teamMember?.name || 'none',
        providerId 
      });
      
      if (!slots.length || !time || !providerId) {
        console.log('Cannot reserve slots: missing required data', { 
          slotsLength: slots.length, 
          time, 
          providerId 
        });
        console.log('❌ Cannot reserve slots: missing required data');
        return { success: false };
      }

      console.log('🔒 Reserving slots in backend:', { slots, time, teamMember });

      // Calculate total duration and services
      const totalDuration = cartItems.reduce((sum, item) => {
        let duration = item.duration || 30;
        // Ensure duration is a number
        if (typeof duration === 'string') {
          duration = parseInt(duration.replace(/[^0-9]/g, '')) || 30;
        } else {
          duration = parseInt(duration) || 30;
        }
        return sum + duration;
      }, 0);
      const services = cartItems.map(item => ({
        serviceId: item.id,
        serviceName: item.name,
        duration: item.duration,
        price: item.price,
        quantity: 1
      }));

      // Prepare staff allocation from slot's schedule
      let staffAllocation = [];
      
      // Debug: Check what we're getting from the slot
      console.log('Slot data for reservation', {
        slotExists: slots && slots.length > 0,
        slotStaffAllocation: slots?.[0]?.staffAllocation,
        slotStaffAllocationType: typeof slots?.[0]?.staffAllocation,
        slotStaffAllocationLength: slots?.[0]?.staffAllocation?.length,
        slotStatus: slots?.[0]?.status,
        slotCanAccommodate: slots?.[0]?.canAccommodateService,
        fullSlotData: slots?.[0] // Log the entire slot object to see what's available
      });

      if (slots && slots.length > 0 && slots[0].staffAllocation && Array.isArray(slots[0].staffAllocation) && slots[0].staffAllocation.length > 0) {
        // Extract staff allocation directly from the slot
        staffAllocation = slots[0].staffAllocation.map(allocation => ({
          serviceId: allocation.serviceId,
          serviceName: allocation.serviceName,
          staffId: allocation.staffId,
          staffName: allocation.staffName,
          startTime: allocation.startTime,
          endTime: allocation.endTime
        }));
        
        console.log('Staff allocation extracted from slot', {
          staffAllocation,
          slotStaffAllocation: slots[0].staffAllocation
        });
      } else {
        // No fallback - if there's no proper staff allocation, the slot should not be selectable
        console.log('No staff allocation found in slot - this should not be selectable', {
          slotsLength: slots?.length,
          hasStaffAllocation: slots?.[0]?.staffAllocation,
          staffAllocationType: typeof slots?.[0]?.staffAllocation,
          staffAllocationLength: slots?.[0]?.staffAllocation?.length,
          slotStatus: slots?.[0]?.status
        });
        
        // Don't create any staff allocation - this slot should be unavailable
        staffAllocation = [];
      }

      const reservationData = {
        providerId: providerId,
        date: selectedDate,
        startTime: time,
        endTime: calculateEndTime(time, totalDuration),
        services: services,
        staffAllocation: staffAllocation,
        sessionId: `session_${Date.now()}` // Unique session ID
      };

      // Call actual backend reservation
      console.log('Calling backend reservation API', { 
        slotsCount: slots.length,
        time,
        providerId,
        totalDuration,
        servicesCount: services.length
      });

      try {
        // Debug user authentication - show full user object
        console.log('User authentication check', {
          userExists: !!user,
          userId: user?.id,
          userIdAlt: user?._id,
          userEmail: user?.email,
          isAuthenticated: !!(user?.id || user?._id),
          fullUserObject: user
        });
        
        // Function to get user ID from JWT token (same as CartContext)
        const getUserIdFromToken = () => {
          try {
            const token = localStorage.getItem('token') || localStorage.getItem('customerToken');
            if (!token) return null;
            
            // Decode JWT token (base64 decode the payload part)
            const payload = token.split('.')[1];
            if (!payload) return null;
            
            const decodedPayload = JSON.parse(atob(payload));
            
            return decodedPayload.id || decodedPayload.userId || decodedPayload._id;
          } catch (error) {
            console.error('❌ Error decoding JWT token:', error);
            return null;
          }
        };
        
        // Get effective user ID using the same pattern as CartContext
        const effectiveUserId = user?.id || user?._id || getUserIdFromToken();
        
        const token = localStorage.getItem('token') || localStorage.getItem('customerToken');
        const tokenExists = !!token;
        
        

        // For now, always use real backend API (remove mock)
        // Check if user is authenticated and has an ID
        const isUserAuthenticated = user && effectiveUserId;
        
        if (!isUserAuthenticated) {
          console.log('User not authenticated - using test customer ID', {
            user: user,
            userId: user?.id,
            user_id: user?._id
          });
          // Use a consistent test customer ID for unauthenticated users in this session
          // If we already have a customer ID from a previous reservation, reuse it
          const testCustomerId = currentCustomerId || 'test-customer-' + Date.now();
          const response = await serviceBookingAPI.reserveSlot(providerId, {
            providerId,
            date: selectedDate,
            startTime: time,
            endTime: calculateEndTime(time, totalDuration),
            customerId: testCustomerId,
            services,
            staffAllocation,
            totalDuration,
            totalPrice: cartItems.reduce((sum, item) => sum + item.price, 0)
          });
          
          // Handle the wrapped response format from successResponse
          const reservationData = response.data?.data || response.data || response;
          
          console.log('Backend reservation successful (test user)', { 
            reservationKeys: reservationData.reservationKeys,
            expiresIn: reservationData.expiresIn,
            customerId: testCustomerId
          });

          console.log('🔒 [FRONTEND] Setting current reservation ID (test user)', {
            reservationKeys: reservationData.reservationKeys,
            expiresIn: reservationData.expiresIn,
            customerId: testCustomerId,
            staffAllocation: reservationData.staffAllocation,
            fullResponse: response
          });
          
          // Log individual team member slot reservations received from backend
          if (response.staffAllocation && response.staffAllocation.length > 0) {
            console.log('🎯 [FRONTEND] Individual Team Member Slot Reservations Received:', {
              staffAllocationCount: response.staffAllocation.length,
              staffAllocation: response.staffAllocation.map((allocation, index) => ({
                serviceIndex: index + 1,
                serviceId: allocation.serviceId,
                teamMemberId: allocation.teamMemberId || allocation.staffId,
                startTime: allocation.startTime,
                endTime: allocation.endTime,
                duration: `${allocation.startTime} - ${allocation.endTime}`
              }))
            });
          } else {
            console.log('⚠️ [FRONTEND] No staffAllocation received from backend - this may cause issues');
            console.log('🔍 [FRONTEND] Full reservation response:', response);
          }
          setCurrentReservationId(response.reservationKeys[0]); // Use first reservation key as primary
          setCurrentReservationKeys(response.reservationKeys); // Store all reservation keys
          setCurrentCustomerId(testCustomerId);
          setCurrentStaffAllocation(response.staffAllocation);
          setReservationExpiresAt(new Date(Date.now() + response.expiresIn * 1000));
          
          // Also store in refs for persistence
          const reservationKey = response.reservationKeys[0];
          console.log('🔒 [FRONTEND] Before setting refs', {
            reservationKey: reservationKey,
            allReservationKeys: response.reservationKeys,
            testCustomerId: testCustomerId,
            currentRefReservationId: currentReservationIdRef.current,
            currentRefCustomerId: currentCustomerIdRef.current
          });
          
          currentReservationIdRef.current = reservationKey;
          currentReservationKeysRef.current = response.reservationKeys;
          currentCustomerIdRef.current = testCustomerId;
          currentStaffAllocationRef.current = response.staffAllocation;
          
          // Force a state update to ensure the reservation ID is properly set
          setTimeout(() => {
            console.log('🔒 [FRONTEND] Verifying state after reservation', {
              currentReservationId: response.reservationKeys[0],
              currentCustomerId: testCustomerId,
              reservationExpiresAt: new Date(Date.now() + response.expiresIn * 1000),
              refReservationId: currentReservationIdRef.current,
              refCustomerId: currentCustomerIdRef.current
            });
          }, 100);
        
        return {
          success: true,
          reservationKey: response.reservationKeys[0],
          expiresIn: response.expiresIn,
          expiresAt: new Date(Date.now() + response.expiresIn * 1000).toISOString()
        };
        } else {
          // User is authenticated
          const response = await serviceBookingAPI.reserveSlot(providerId, {
            providerId,
            date: selectedDate,
            startTime: time,
            endTime: calculateEndTime(time, totalDuration),
            customerId: effectiveUserId,
            services,
            staffAllocation,
            totalDuration,
            totalPrice: cartItems.reduce((sum, item) => sum + item.price, 0)
          });

        console.log('🔒 [FRONTEND] Raw API response received:', response);
        console.log('🔒 [FRONTEND] Response type:', typeof response);
        console.log('🔒 [FRONTEND] Response keys:', Object.keys(response || {}));
        
        // Handle the wrapped response format from successResponse
        const reservationData = response.data?.data || response.data || response;
        
        console.log('🔒 [FRONTEND] Processed reservation data:', reservationData);
        console.log('🔒 [FRONTEND] Reservation data keys:', Object.keys(reservationData || {}));
        
        console.log('Backend reservation successful', { 
          reservationKeys: reservationData.reservationKeys,
          expiresIn: reservationData.expiresIn,
          staffAllocation: reservationData.staffAllocation
        });

        console.log('🔒 [FRONTEND] Setting current reservation ID (authenticated user)', {
          reservationKey: reservationData.reservationKeys[0],
          expiresIn: reservationData.expiresIn,
          customerId: effectiveUserId,
          staffAllocation: reservationData.staffAllocation
        });
        
        // Log individual team member slot reservations received from backend
        if (reservationData.staffAllocation && reservationData.staffAllocation.length > 0) {
          console.log('🎯 [FRONTEND] Individual Team Member Slot Reservations Received (Authenticated):', {
            staffAllocationCount: reservationData.staffAllocation.length,
            staffAllocation: reservationData.staffAllocation.map((allocation, index) => ({
              serviceIndex: index + 1,
              serviceId: allocation.serviceId,
              teamMemberId: allocation.teamMemberId || allocation.staffId,
              startTime: allocation.startTime,
              endTime: allocation.endTime,
              duration: `${allocation.startTime} - ${allocation.endTime}`
            }))
          });
        } else {
          console.log('⚠️ [FRONTEND] No staffAllocation received from backend (Authenticated) - this may cause issues');
        }
        setCurrentReservationId(reservationData.reservationKeys[0]); // Use first reservation key as primary
        setCurrentReservationKeys(reservationData.reservationKeys); // Store all reservation keys
        setCurrentCustomerId(effectiveUserId);
        setCurrentStaffAllocation(reservationData.staffAllocation);
        setReservationExpiresAt(new Date(Date.now() + reservationData.expiresIn * 1000));
        
        // Also store in refs for persistence
        const reservationKey = reservationData.reservationKeys[0];
        console.log('🔒 [FRONTEND] Before setting refs (authenticated user)', {
          reservationKey: reservationKey,
          allReservationKeys: reservationData.reservationKeys,
          userId: effectiveUserId,
          currentRefReservationId: currentReservationIdRef.current,
          currentRefCustomerId: currentCustomerIdRef.current
        });
        
        currentReservationIdRef.current = reservationKey;
        currentReservationKeysRef.current = reservationData.reservationKeys;
        currentCustomerIdRef.current = effectiveUserId;
        currentStaffAllocationRef.current = reservationData.staffAllocation;
        
        console.log('🔒 [FRONTEND] After setting refs (authenticated user)', {
          refReservationId: currentReservationIdRef.current,
          refCustomerId: currentCustomerIdRef.current,
          reservationKey: reservationKey
        });
        
        return {
          success: true,
          reservationKey: reservationData.reservationKeys[0],
          expiresIn: reservationData.expiresIn,
          expiresAt: new Date(Date.now() + reservationData.expiresIn * 1000).toISOString()
        };
        }
      } catch (reservationError) {
        console.log('🔒 [FRONTEND] Backend reservation failed', { 
          error: reservationError.message,
          errorType: reservationError.constructor.name,
          response: reservationError.response?.data,
          stack: reservationError.stack
        });
        console.error('❌ Backend reservation failed:', reservationError);
        return { success: false };
      }
    } catch (error) {
      console.log('🔒 [FRONTEND] Error reserving slots', { 
        error: error.message,
        errorType: error.constructor.name,
        stack: error.stack 
      });
      console.error('❌ Error reserving slots:', error);
      return { success: false };
    }
  };

  // Release selected slots (frontend state)
  const releaseSelectedSlots = (stopTimer = true) => {
    console.log('🔓 [FRONTEND] Releasing selected slots', {
      selectedSlotsCount: selectedSlots.length,
      selectedTime,
      currentReservationId,
      timerVisible: timerState.isVisible,
      stopTimer
    });
    
    // Clear frontend state
    setSelectedTime('');
    setSelectedSlots([]);
    setSelectedTeamMember(null);
    
    // Clear localStorage
    localStorage.removeItem(`availability_${providerId}_selectedTime`);
    localStorage.removeItem(`availability_${providerId}_selectedSlots`);
    localStorage.removeItem(`availability_${providerId}_selectedTeamMember`);
    localStorage.removeItem(`availability_${providerId}_selectedDate`);
    
    console.log('🔓 [FRONTEND] Frontend state cleared', {
      selectedSlotsCount: 0,
      selectedTime: '',
      timerStopped: stopTimer
    });
    
    // Stop timer only if requested
    if (stopTimer) {
    stopGlobalTimer();
    }
    
    // Release backend reservation if exists
    if (currentReservationId) {
      console.log('🔓 [FRONTEND] Releasing backend reservation', {
        reservationId: currentReservationId
      });
      releaseReservedSlots();
    } else {
      console.log('🔓 [FRONTEND] No backend reservation to release', {});
    }
  };

  // Release reserved slots from backend
  const releaseReservedSlots = async () => {
    try {
      // Use refs as fallback if state is null
      const reservationKeys = currentReservationKeys.length > 0 ? currentReservationKeys : currentReservationKeysRef.current;
      const customerId = currentCustomerId || currentCustomerIdRef.current;
      
      if (reservationKeys && reservationKeys.length > 0) {
        // Extract customer ID from reservation key if we don't have it stored
        let customerIdToUse = customerId;
        if (!customerIdToUse && reservationKeys[0]) {
          // Extract customer ID from reservation key format: providerId_date_startTime_endTime_customerId
          const parts = reservationKeys[0].split('_');
          if (parts.length >= 5) {
            customerIdToUse = parts.slice(4).join('_'); // In case customer ID contains underscores
          }
        }
        
        console.log('🔓 [FRONTEND] Calling backend release API for all reservations', {
          reservationKeysCount: reservationKeys.length,
          reservationKeys: reservationKeys,
          customerId: customerIdToUse,
          extractedFromKey: !customerId
        });
        
        // Release all individual reservations
        const releasePromises = reservationKeys.map(async (reservationKey) => {
          try {
            const response = await serviceBookingAPI.releaseSlot(reservationKey, customerIdToUse);
        if (response.success) {
              console.log('✅ [FRONTEND] Released reservation:', reservationKey);
              return { success: true, key: reservationKey };
        } else {
              console.log('❌ [FRONTEND] Failed to release reservation:', reservationKey, response.error);
              return { success: false, key: reservationKey, error: response.error };
            }
          } catch (error) {
            console.log('❌ [FRONTEND] Error releasing reservation:', reservationKey, error.message);
            return { success: false, key: reservationKey, error: error.message };
          }
        });
        
        const results = await Promise.all(releasePromises);
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;
        
        console.log('🔓 [FRONTEND] Release results:', {
          total: reservationKeys.length,
          successful: successCount,
          failed: failCount,
          results: results
        });
        
        if (successCount > 0) {
          console.log('✅ [FRONTEND] Successfully released reservations:', successCount);
        }
        if (failCount > 0) {
          console.log('❌ [FRONTEND] Failed to release reservations:', failCount);
        }
      } else {
        console.log('🔓 [FRONTEND] No reservation keys to release', {});
      }
    } catch (error) {
      console.log('❌ [FRONTEND] Error releasing backend slots', { 
        error: error.message,
        stack: error.stack 
      });
    } finally {
      // Don't clear reservation state here - it will be cleared when new reservation is created
      console.log('🔓 [FRONTEND] Release operation completed', {
        currentReservationId,
        currentCustomerId,
        reservationExpiresAt,
        reason: 'releaseReservedSlots completed - state will be updated by new reservation'
      });
    }
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    return endDate.toTimeString().slice(0, 5);
  };

  // Convert 24-hour format to 12-hour AM/PM format
  const formatTimeToAMPM = (time24) => {
    if (!time24 || typeof time24 !== 'string') {
      console.error('❌ [FORMAT TIME] Invalid time input:', time24);
      return 'Invalid Time';
    }
    
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Location search functions
  const handleSearchLocation = async () => {
    if (!locationSearchQuery.trim()) return;

    try {
      setLocationLoading(true);
      setLocationError('');
      // This would typically call a geocoding service
      // For now, we'll simulate with mock data
      const mockSuggestions = [
        { address: `${locationSearchQuery}, New York, NY`, latitude: 40.7128, longitude: -74.0060 },
        { address: `${locationSearchQuery}, Los Angeles, CA`, latitude: 34.0522, longitude: -118.2437 },
        { address: `${locationSearchQuery}, Chicago, IL`, latitude: 41.8781, longitude: -87.6298 },
        { address: `${locationSearchQuery}, Miami, FL`, latitude: 25.7617, longitude: -80.1918 },
        { address: `${locationSearchQuery}, Seattle, WA`, latitude: 47.6062, longitude: -122.3321 },
      ];
      setLocationSuggestions(mockSuggestions);
    } catch (error) {
      setLocationError('Failed to search for location');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSelectLocation = (suggestion) => {
    setServiceAddress(suggestion.address);
    setLocationSuggestions([]);
    setLocationSearchQuery(suggestion.address);
  };

  // WebSocket event handlers
  const handleSlotReserved = (data) => {
    console.log('🔒 Slot reserved by another user:', data);
    // Refresh availability to show updated slots
    refreshAvailability();
  };

  const handleSlotReleased = (data) => {
    console.log('🔓 Slot released by another user:', data);
    // Refresh availability to show updated slots
    refreshAvailability();
  };

  const handleSlotBooked = (data) => {
    console.log('📅 Slot booked by another user:', data);
    // Refresh availability to show updated slots
    refreshAvailability();
  };

  const handleProviderAvailabilityUpdate = (data) => {
    console.log('📊 Provider availability updated:', data);
    // Refresh availability to show updated slots
    refreshAvailability();
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Initial debug logging
      console.log('Availability page loaded', {
        providerId,
        selectedDate,
        isReschedule,
        userAuthenticated: !!user,
        userId: user?.id
      });
      
      try {
        // Create minimal provider object from available data (no API call needed)
        console.log('🔍 Creating minimal provider object for ID:', providerId);
        const providerData = {
          id: providerId,
          name: 'Provider', // Will be updated from cart items if available
          type: 'Store', // Default type
          providerType: 'salon' // Default to salon
        };
        console.log('🔍 Minimal provider data created:', providerData);
    
    // Use reschedule services if in reschedule mode, otherwise use provider cart items
    let items;
        if (isReschedule && originalBooking?.services) {
          // Fetch actual service details to get duration
          const serviceDetails = await Promise.all(
            originalBooking.services.map(async (service) => {
              try {
                const response = await serviceAPI.getServiceDetails(service.id || service.serviceId, providerId);
                if (response.success && response.data) {
                  return {
                    id: service.id || service.serviceId,
                    serviceId: service.id || service.serviceId,
                    name: service.name || service.serviceName,
                    serviceName: service.name || service.serviceName,
            price: service.price,
                    duration: parseInt(response.data.duration) || 30, // Get actual duration from service details, ensure it's a number
                    image: service.image || response.data.serviceImage || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop',
                    // Store team member information (not displayed)
                    teamMember: originalBooking.teamMember || null,
                    teamMemberId: originalBooking.teamMember?.id || null,
                    teamMemberName: originalBooking.teamMember?.name || null
                  };
                }
              } catch (error) {
                console.error('Error fetching service details:', error);
              }
              
              // Fallback if service details fetch fails
              return {
                id: service.id || service.serviceId,
                serviceId: service.id || service.serviceId,
                name: service.name || service.serviceName,
                serviceName: service.name || service.serviceName,
                price: service.price,
                duration: parseInt(service.duration) || 30, // Use original duration if available, ensure it's a number
                image: service.image || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=80&h=80&fit=crop',
                // Store team member information (not displayed)
                teamMember: originalBooking.teamMember || null,
                teamMemberId: originalBooking.teamMember?.id || null,
                teamMemberName: originalBooking.teamMember?.name || null
              };
            })
          );
          
          items = serviceDetails.filter(item => item !== null);
          
          // Debug: Log reschedule service mapping
          console.log('Reschedule service mapping', {
            originalServices: originalBooking.services,
            mappedItems: items,
            teamMemberInfo: {
              hasTeamMember: !!originalBooking.teamMember,
              teamMemberName: originalBooking.teamMember?.name,
              teamMemberId: originalBooking.teamMember?.id
            }
          });
    } else {
          items = await getCartItems(providerId);
    }
    
    console.log('🔍 Provider data received:', providerData);
    console.log('🔍 Provider type:', providerData?.type, 'Provider providerType:', providerData?.providerType);
    console.log('🔍 Provider image fields:', {
      photoURL: providerData?.photoURL,
      profilePhotoUrl: providerData?.profilePhotoUrl,
      profilePicture: providerData?.profilePicture,
      logoUrl: providerData?.logoUrl,
      businessImage: providerData?.businessImage,
      image: providerData?.image
    });
    
    console.log('🔍 Provider address fields:', {
      address: providerData?.address,
      street: providerData?.street,
      city: providerData?.city,
      state: providerData?.state,
      zipCode: providerData?.zipCode,
      country: providerData?.country,
      fullAddress: providerData?.fullAddress
    });
    
    if (!providerData) {
      console.error('🔍 Provider data is null - API call may have failed');
      console.error('🔍 This means the provider API is not working correctly');
    }
    
    setProvider(providerData);
    setCartItems(items);
    
    // Debug: Log final cart items and provider data
    console.log('Final cart items and provider data', {
      cartItems: items,
      provider: providerData,
      isReschedule,
      rescheduleFee
    });
    
        // Load real availability slots for today
        const serviceIds = items.map(item => item.id);
        const realSlots = await fetchProviderAvailability(providerId, serviceIds, selectedDate);
        setAvailableSlots(realSlots);
    
        // Connect to WebSocket and subscribe to provider slot updates
        const token = localStorage.getItem('token');
        websocketService.connect(token);
        
        // Subscribe to slot updates for this provider and date
        websocketService.addEventListener('slotReserved', handleSlotReserved);
        websocketService.addEventListener('slotReleased', handleSlotReleased);
        websocketService.addEventListener('slotBooked', handleSlotBooked);
        websocketService.addEventListener('providerAvailabilityUpdate', handleProviderAvailabilityUpdate);
        
        // Subscribe to provider slot updates
        websocketService.subscribeToProviderSlots(providerId, selectedDate);
        
      } catch (error) {
        console.error('🔍 Error loading data:', error);
        console.error('🔍 Error details:', {
          message: error.message,
          stack: error.stack,
          providerId: providerId
        });
        setError('Failed to load provider data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();

    // Cleanup WebSocket listeners on unmount
    return () => {
      websocketService.removeEventListener('slotReserved', handleSlotReserved);
      websocketService.removeEventListener('slotReleased', handleSlotReleased);
      websocketService.removeEventListener('slotBooked', handleSlotBooked);
      websocketService.removeEventListener('providerAvailabilityUpdate', handleProviderAvailabilityUpdate);
      
      // Unsubscribe from provider slot updates
      websocketService.unsubscribeFromProviderSlots(providerId, selectedDate);
    };
  }, [providerId, selectedDate, isReschedule, originalBooking]);

  // Timer restoration is now handled by TimerContext

  // Load saved state after availability is loaded
  useEffect(() => {
    if (availableSlots.length > 0 && !loading) {
      // Only load state if we don't already have selected slots
      if (selectedSlots.length === 0) {
        // Add a small delay to ensure TimerContext has had time to restore timer state
        const timer = setTimeout(() => {
        loadSelectedSlotsState();
        }, 100);
        
        return () => clearTimeout(timer);
      }
    }
  }, [availableSlots, loading, timerState.isVisible]); // Added timerState.isVisible to re-run when timer state changes

  // Restore slots when date changes (in case date was restored but slots weren't)
  useEffect(() => {
    if (selectedDate && availableSlots.length > 0 && !loading && selectedSlots.length === 0) {
      // Check if there are saved slots for this date
      const savedSlots = localStorage.getItem(`availability_${providerId}_selectedSlots`);
      const savedTime = localStorage.getItem(`availability_${providerId}_selectedTime`);
      const savedTeamMember = localStorage.getItem(`availability_${providerId}_selectedTeamMember`);
      
      if (savedSlots || savedTime || savedTeamMember) {
        console.log('🔄 [STATE] Restoring slots after date change', {
          selectedDate: selectedDate,
          savedSlots: savedSlots,
          savedTime: savedTime,
          savedTeamMember: savedTeamMember
        });
        
        if (savedSlots) {
          const slots = JSON.parse(savedSlots);
          setSelectedSlots(slots);
        }
        
        if (savedTime) {
          setSelectedTime(savedTime);
        }
        
        if (savedTeamMember) {
          setSelectedTeamMember(savedTeamMember);
        }
      }
    }
  }, [selectedDate, availableSlots.length, loading, selectedSlots.length, providerId]);

  // Track previous timer state to detect expiration
  const wasTimerVisible = useRef(false);

  // Clear selected slots when timer expires (becomes invisible)
  useEffect(() => {
    // Only clear slots if timer was previously visible and now it's not
    if (wasTimerVisible.current && !timerState.isVisible && selectedSlots.length > 0) {
      console.log('⏰ [TIMER] Timer expired - clearing selected slots', {
        timerVisible: timerState.isVisible,
        selectedSlotsCount: selectedSlots.length,
        selectedTime: selectedTime
      });
      
      // Clear frontend state
      setSelectedTime('');
      setSelectedSlots([]);
      setSelectedTeamMember(null);
      
      // Clear localStorage
      localStorage.removeItem(`availability_${providerId}_selectedTime`);
      localStorage.removeItem(`availability_${providerId}_selectedSlots`);
      localStorage.removeItem(`availability_${providerId}_selectedTeamMember`);
      localStorage.removeItem(`availability_${providerId}_selectedDate`);
      
      // Clear reservation state
      setCurrentReservationId(null);
      setCurrentCustomerId(null);
    }
    
    // Update the ref to track current timer state
    wasTimerVisible.current = timerState.isVisible;
  }, [timerState.isVisible, selectedSlots.length, selectedTime, providerId]);

  // Commented out auto-date change to prevent issues
  // useEffect(() => {
  //   if (selectedDate) {
  //     const today = new Date().toISOString().split('T')[0];
  //     if (selectedDate === today) {
  //       setSelectedDate(availableSlots[1]?.date || '');
  //     }
  //   }
  // }, [availableSlots, selectedDate]);

  // Update WebSocket subscription when date changes
  useEffect(() => {
    if (providerId && selectedDate) {
      // Unsubscribe from previous date
      websocketService.unsubscribeFromProviderSlots(providerId, selectedDate);
      
      // Subscribe to new date
      websocketService.subscribeToProviderSlots(providerId, selectedDate);
    }
  }, [selectedDate, providerId]);

  // Clean up timer when component unmounts
  useEffect(() => {
    return () => {
      // Don't stop the timer on unmount - let it persist across route changes
      // The timer should only be stopped when explicitly released or expired
      console.log('🔄 [TIMER] Component unmounting - keeping timer running for persistence', {
        selectedSlotsLength: selectedSlots.length
      });
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Debug: Monitor selectedSlots state changes
  useEffect(() => {
    if (selectedSlots.length > 0) {
      console.log('STATE: selectedSlots updated to:', selectedSlots.length);
    }
  }, [selectedSlots]);

  // Debug: Monitor timerState changes
  useEffect(() => {
    console.log('Timer state changed', {
      isVisible: timerState.isVisible,
      seconds: timerState.seconds,
      title: timerState.title
    });
  }, [timerState]);

  // Debug: Monitor selectedSlots changes
  useEffect(() => {
    console.log('selectedSlots state changed', {
      count: selectedSlots.length,
      slots: selectedSlots.map(s => s.startTime)
    });
  }, [selectedSlots]);

  // Debug: Log when slots are rendered
  useEffect(() => {
    if (availableSlots.length > 0) {
      const daySlots = availableSlots.find(day => day.date === selectedDate);
      if (daySlots && daySlots.slots) {
        console.log('Slots rendered for date', {
          date: selectedDate,
          totalSlots: daySlots.slots.length,
          availableSlots: daySlots.slots.map(s => s.startTime).slice(0, 10), // First 10 slots
          selectedSlotsCount: selectedSlots.length,
          timerVisible: timerState.isVisible
        });
      }
    }
  }, [availableSlots, selectedDate, selectedSlots.length, timerState.isVisible]);


  // Handle timer expiration - removed the problematic useEffect
  // Timer expiration is now handled by the timer callback in handleTimeSelect

  const handleDateSelect = async (date) => {
    if (!date) return;
    
    const dateString = date.toISOString().split('T')[0];
    
    // Get today's date in local timezone (not UTC)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const today = `${year}-${month}-${day}`;
    
    // Prevent selection of past dates
    if (dateString < today) {
      console.log('⚠️ [DATE SELECT] Cannot select past date:', dateString);
      return;
    }
    
    setSelectedDate(dateString);
    
    // Clear any previous no slots message and unavailable services when date changes
    setNoSlotsMessage('');
    setUnavailableServices([]);
    setAvailableSlots([]); // Clear slots to show loading state
    
    // Release selected slots when date changes
    if (selectedTime || selectedSlots.length > 0) {
      releaseSelectedSlots();
    }
    
    // Note: Data loading is handled by the main useEffect that depends on selectedDate
    // No need to duplicate the data loading here
  };

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  // Helper function to convert minutes to time string
  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to add time to a time string
  const addTime = (timeString, minutes) => {
    const [hours, mins] = timeString.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  // Helper function to subtract time from a time string
  const subtractTime = (timeString, minutes) => {
    const [hours, mins] = timeString.split(':').map(Number);
    const totalMinutes = hours * 60 + mins - minutes;
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  // Helper function to calculate time difference in minutes
  const calculateTimeDifference = (startTime, endTime) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    return endMinutes - startMinutes;
  };

  // Helper function to check if a slot is truly available (not reserved or booked)
  const isSlotAvailable = (slot) => {
    return slot.status === 'available' && 
           slot.canAccommodateService &&
           slot.status !== 'reserved' &&
           slot.status !== 'booked';
  };

  

  // Helper function to find last available slot for multi-service booking considering team member constraints
  const findLastAvailableSlotForMultiService = () => {
    console.log('🔍 [FRONTEND] findLastAvailableSlotForMultiService called');
    
    const currentDaySlots = availableSlots.find(day => day.date === selectedDate);
    if (!currentDaySlots || !currentDaySlots.slots) {
      console.log('🔍 [FRONTEND] No day slots found for date:', selectedDate);
      return null;
    }
    
    const availableSlotsForDay = currentDaySlots.slots.filter(slot => 
      isSlotAvailable(slot)
    );
    
    console.log('🔍 [FRONTEND] Available slots for day:', availableSlotsForDay.length);
    console.log('🔍 [FRONTEND] Team members data:', teamMembers);
    
    if (availableSlotsForDay.length === 0) {
      console.log('🔍 [FRONTEND] No available slots found');
      return null;
    }
    
    // Use team member shift data to calculate the latest possible start time
    let latestPossibleStart = null;
    
    // Get team members with their shift information
    const teamMembersWithShifts = teamMembers.filter(member => 
      member.shift && member.shift.startTime && member.shift.endTime
    );
    
    console.log('🔍 [FRONTEND] Team members with shifts:', teamMembersWithShifts.map(m => ({
      name: m.firstName + ' ' + m.lastName,
      shift: `${m.shift.startTime} - ${m.shift.endTime}`,
      qualifiedServices: m.qualifiedServices?.map(s => s.serviceName) || []
    })));
    
    if (teamMembersWithShifts.length === 0) {
      console.log('🔍 [FRONTEND] No team members with shift data found');
      return null;
    }
    
    // For multi-service booking, we need to find the constraint that limits the latest start time
    for (let i = 0; i < cartItems.length; i++) {
      const service = cartItems[i];
      
      // Find team members who can do this service
      const qualifiedMembers = teamMembersWithShifts.filter(member => 
        member.qualifiedServices?.some(qs => qs.id === service.id || qs._id === service.id)
      );
      
      console.log(`🔍 [FRONTEND] Service ${service.name} qualified members:`, qualifiedMembers.map(m => ({
        name: m.firstName + ' ' + m.lastName,
        shift: `${m.shift.startTime} - ${m.shift.endTime}`
      })));
      
      if (qualifiedMembers.length > 0) {
        // Find the latest possible start time for this service based on team member shifts
        let serviceLatestStart = null;
        
        for (const member of qualifiedMembers) {
          // Calculate latest start time for this member and service
          const memberLatestStart = subtractTime(member.shift.endTime, service.duration);
          
          // For multi-service, we need to account for previous services
          if (i > 0) {
            const previousServicesDuration = cartItems.slice(0, i).reduce((total, s) => total + s.duration, 0);
            const adjustedLatestStart = subtractTime(memberLatestStart, previousServicesDuration);
            
            if (!serviceLatestStart || timeToMinutes(adjustedLatestStart) < timeToMinutes(serviceLatestStart)) {
              serviceLatestStart = adjustedLatestStart;
            }
          } else {
            if (!serviceLatestStart || timeToMinutes(memberLatestStart) < timeToMinutes(serviceLatestStart)) {
              serviceLatestStart = memberLatestStart;
            }
          }
        }
        
        // Take the most restrictive constraint across all services
        if (!latestPossibleStart || timeToMinutes(serviceLatestStart) < timeToMinutes(latestPossibleStart)) {
          latestPossibleStart = serviceLatestStart;
        }
      }
    }
    
    if (!latestPossibleStart) {
      console.log('🔍 [FRONTEND] Could not determine latest possible start time from team member shifts');
      return null;
    }
    
    console.log('🔍 [FRONTEND] Latest possible start time from team member shifts:', latestPossibleStart);
    
    // Find slots that are at or before the latest possible start time
    const validSlots = availableSlotsForDay.filter(slot => {
      const slotTime = timeToMinutes(slot.startTime);
      const latestTime = timeToMinutes(latestPossibleStart);
      const isValid = slotTime <= latestTime;
      console.log(`🔍 [FRONTEND] Slot ${slot.startTime}: ${slotTime} <= ${latestTime} = ${isValid}`);
      return isValid;
    });
    
    console.log('🔍 [FRONTEND] Valid slots after filtering:', validSlots.length);
    
    if (validSlots.length === 0) {
      console.log('🔍 [FRONTEND] No valid slots found after filtering, trying fallback');
      
      // Fallback: if no slots are available at or before the calculated time, 
      // find the latest available slot that can accommodate the service
      const fallbackSlots = availableSlotsForDay.filter(slot => slot.canAccommodateService);
      if (fallbackSlots.length > 0) {
        const sortedFallback = fallbackSlots.sort((a, b) => {
          const timeA = timeToMinutes(a.startTime);
          const timeB = timeToMinutes(b.startTime);
          return timeB - timeA;
        });
        const fallbackResult = sortedFallback[0].startTime;
        console.log('🔍 [FRONTEND] Fallback slot found:', fallbackResult);
        return fallbackResult;
      }
      
      return null;
    }
    
    // Sort by time descending to get the latest valid slot
    const sortedSlots = validSlots.sort((a, b) => {
      const timeA = timeToMinutes(a.startTime);
      const timeB = timeToMinutes(b.startTime);
      return timeB - timeA;
    });
    
    const result = sortedSlots[0].startTime;
    console.log('🔍 [FRONTEND] Latest valid slot found:', result);
    
    // Return the latest valid slot
    return result;
  };

  // Function to remove unavailable services from cart and refetch slots
  const handleRemoveUnavailableServices = async () => {
    console.log('🔍 [REMOVE SERVICES] Button clicked!');
    console.log('🔍 [REMOVE SERVICES] unavailableServices:', unavailableServices);
    console.log('🔍 [REMOVE SERVICES] current cartItems:', cartItems);
    
    if (unavailableServices.length === 0) {
      console.log('⚠️ [REMOVE SERVICES] No unavailable services to remove');
      return;
    }
    
    console.log('🔍 [REMOVE SERVICES] Setting loading to true');
    setRemovingServices(true);
    
    try {
      // Remove unavailable services from actual cart using cart context
      for (const serviceName of unavailableServices) {
        const serviceToRemove = cartItems.find(item => 
          (item.serviceName || item.name) === serviceName
        );
        if (serviceToRemove && serviceToRemove.cartItemId) {
          console.log(`🔍 [REMOVE SERVICES] Removing ${serviceName} from cart with ID: ${serviceToRemove.cartItemId}`);
          await removeFromCart(serviceToRemove.cartItemId);
        }
      }
      
      // Remove unavailable services from local cart state
      const updatedCartItems = cartItems.filter(item => {
        const serviceName = item.serviceName || item.name;
        const shouldKeep = !unavailableServices.includes(serviceName);
        console.log(`🔍 [REMOVE SERVICES] Checking item "${serviceName}": shouldKeep = ${shouldKeep}`);
        return shouldKeep;
      });
      
      console.log('🔍 [REMOVE SERVICES] Updated cart items:', updatedCartItems);
      setCartItems(updatedCartItems);
      
      // Clear the no slots message and unavailable services
      setNoSlotsMessage('');
      setUnavailableServices([]);
      
      // Refetch slots with updated cart
      if (updatedCartItems.length > 0) {
        console.log('🔍 [REMOVE SERVICES] Refetching slots for remaining services...');
        const serviceIds = updatedCartItems.map(item => item.id);
        const realSlots = await fetchProviderAvailability(providerId, serviceIds, selectedDate);
        setAvailableSlots(realSlots);
      } else {
        console.log('🔍 [REMOVE SERVICES] No services remaining, clearing slots');
        setAvailableSlots([]);
      }
      
      console.log('✅ [REMOVE SERVICES] Successfully removed unavailable services');
      
    } catch (error) {
      console.error('❌ [REMOVE SERVICES] Error removing unavailable services:', error);
    } finally {
      console.log('🔍 [REMOVE SERVICES] Setting loading to false');
      setRemovingServices(false);
    }
  };

  // Handle time slot selection
  const handleTimeSelect = async (time, slots, teamMember) => {
    // Prevent multiple simultaneous slot selections
    if (slotSelectingRef.current) {
      showSnackbar('Please wait, processing your previous selection...', 'info');
      return;
    }
    
    slotSelectingRef.current = true;
    setSlotSelecting(true);
    
    try {
      // Find the selected slot
      const daySlots = availableSlots.find(day => day.date === selectedDate);
      if (!daySlots) {
        slotSelectingRef.current = false;
        setSlotSelecting(false);
        return;
      }

      const selectedSlot = daySlots.slots.find(slot => slot.startTime === time);
      if (!selectedSlot) {
        slotSelectingRef.current = false;
        setSlotSelecting(false);
        return;
      }
      
      // Extract team member information from slot's schedule
      let extractedTeamMember = teamMember;
      if (!extractedTeamMember && selectedSlot.schedule && selectedSlot.schedule.services && selectedSlot.schedule.services.length > 0) {
        const firstService = selectedSlot.schedule.services[0];
        if (firstService.staffId && firstService.staffName) {
          extractedTeamMember = {
            id: firstService.staffId,
            name: firstService.staffName
          };
        }
      }
      
      // Release any previously reserved slots (don't wait - do in background)
      const reservationId = currentReservationId || currentReservationIdRef.current;
      if (reservationId) {
        stopGlobalTimer();
        // Release in background - don't block UI
        releaseReservedSlots().catch(() => {});
        // Clear state immediately
        setCurrentReservationId(null);
        setCurrentReservationKeys([]);
        setCurrentCustomerId(null);
        setReservationExpiresAt(null);
        currentReservationIdRef.current = null;
        currentReservationKeysRef.current = [];
        currentCustomerIdRef.current = null;
      }

      // Reserve the new slot immediately
      const reservationResult = await reserveSelectedSlots([selectedSlot], time, extractedTeamMember);
      
        if (reservationResult && reservationResult.success) {
          // Set the selected slot state after successful reservation
          setSelectedTime(time);
          setSelectedSlots([selectedSlot]);
          setSelectedTeamMember(extractedTeamMember);
          
          // Save to localStorage for persistence
          try {
            localStorage.setItem(`availability_${providerId}_selectedDate`, selectedDate);
            localStorage.setItem(`availability_${providerId}_selectedTime`, time);
            localStorage.setItem(`availability_${providerId}_selectedSlots`, JSON.stringify([selectedSlot]));
            if (extractedTeamMember) {
              localStorage.setItem(`availability_${providerId}_selectedTeamMember`, JSON.stringify(extractedTeamMember));
            }
          } catch (storageError) {
            // Don't fail if localStorage fails
          }
          
          // Start 5-minute reservation timer
          const slotDurationText = `${formatTimeToAMPM(selectedSlot.startTime)} - ${formatTimeToAMPM(selectedSlot.endTime)}`;
          
          // Timer expiry callback
          const onTimerExpire = async () => {
            await releaseReservedSlots();
            setCurrentReservationId(null);
            setCurrentReservationKeys([]);
            setCurrentCustomerId(null);
            setReservationExpiresAt(null);
            currentReservationIdRef.current = null;
            currentReservationKeysRef.current = [];
            currentCustomerIdRef.current = null;
            setSelectedTime('');
            setSelectedSlots([]);
            setSelectedTeamMember(null);
            localStorage.removeItem(`availability_${providerId}_selectedTime`);
            localStorage.removeItem(`availability_${providerId}_selectedSlots`);
            localStorage.removeItem(`availability_${providerId}_selectedTeamMember`);
            localStorage.removeItem(`availability_${providerId}_selectedDate`);
          };
          
          startGlobalTimer(`Slot Reserved: ${slotDurationText}`, onTimerExpire);
        } else {
          // Reset selection if reservation failed
          setSelectedTime('');
          setSelectedSlots([]);
          setSelectedTeamMember(null);
        }
      } catch (error) {
        // Reset selection if error occurred
        setSelectedTime('');
        setSelectedSlots([]);
        setSelectedTeamMember(null);
      } finally {
        slotSelectingRef.current = false;
        setSlotSelecting(false);
      }
  };



  // Helper function to set new slots and start timer
  const setNewSlots = (timeSlot, consecutiveSlots, assignedTeamMember) => {
    // Set the new selected slots first
    setSelectedTime(timeSlot.startTime);
    setSelectedSlots(consecutiveSlots);
    setSelectedTeamMember(assignedTeamMember);
    
    // Reset the intentionally released flag
    setSlotsIntentionallyReleased(false);
    
    // Save the selected slots state
    saveSelectedSlotsState(consecutiveSlots, timeSlot.startTime, assignedTeamMember);
    
    // Timer is already started by the main handleTimeSelect function
    // No need to start it again here
  };

  // Handle continue to checkout
  const handleContinueToCheckout = () => {
    if (!selectedTime || selectedSlots.length === 0) {
      // Show error in smart dialog instead of console log
      // No slot selected - cannot proceed
      return;
    }

    // Get the full slot objects with staffAllocation from the availability system
    const daySlots = availableSlots.find(day => day.date === selectedDate);
    const fullSlots = daySlots ? daySlots.slots.filter(slot => 
      selectedSlots.some(selectedSlot => 
        (typeof selectedSlot === 'string' ? selectedSlot : selectedSlot.startTime) === slot.startTime
      )
    ) : selectedSlots;

    // Calculate end time based on total duration
    const endTime = calculateEndTime(selectedTime, cartItems.reduce((sum, item) => sum + item.duration, 0));

    // Prepare complete schedule data
    const completeSchedule = {
      date: selectedDate,
      time: selectedTime,
      endTime: endTime,
      slots: fullSlots,
      teamMember: selectedTeamMember,
      bookingType: provider?.type === 'Store' ? (provider?.providerType === 'salon' ? 'In Salon' : 'At Home') : (provider?.type === 'Pros' ? 'At Home' : 'In Salon'),
      duration: cartItems.reduce((sum, item) => sum + item.duration, 0),
      reservationId: currentReservationId,
      staffAllocation: currentStaffAllocation,
      // Add complete service schedule details
      serviceSchedule: currentStaffAllocation?.map((allocation, index) => ({
        serviceIndex: index + 1,
        serviceId: allocation.serviceId,
        serviceName: allocation.serviceName,
        staffId: allocation.staffId,
        staffName: allocation.staffName,
        startTime: allocation.startTime,
        endTime: allocation.endTime,
        duration: `${allocation.startTime} - ${allocation.endTime}`,
        // Include the cart item details for this service
        cartItem: cartItems[index] || null
      })) || [],
      // Add provider details
      provider: {
        id: provider?.id,
        name: provider?.name,
        type: provider?.type,
        address: provider?.address,
        location: provider?.location
      },
      // Add total pricing
      totalPrice: cartItems.reduce((sum, item) => sum + item.price, 0),
      totalDuration: cartItems.reduce((sum, item) => sum + item.duration, 0)
    };

    // Log what staffAllocation is being passed to checkout
    console.log('🚀 [FRONTEND] Navigating to Checkout with Complete Data:', {
      currentStaffAllocation: currentStaffAllocation,
      staffAllocationCount: currentStaffAllocation?.length || 0,
      staffAllocationDetails: currentStaffAllocation?.map((allocation, index) => ({
        serviceIndex: index + 1,
        serviceId: allocation.serviceId,
        serviceName: allocation.serviceName,
        teamMemberId: allocation.teamMemberId || allocation.staffId,
        teamMemberName: allocation.staffName,
        startTime: allocation.startTime,
        endTime: allocation.endTime,
        duration: `${allocation.startTime} - ${allocation.endTime}`
      })) || [],
      completeSchedule: completeSchedule,
      selectedSlots: selectedSlots,
      cartItems: cartItems
    });

    // Navigate to checkout with complete reservation data
    navigate('/checkout', {
      state: {
        provider: provider,
        cartItems: cartItems,
        selectedSlot: completeSchedule,
        isReschedule: isReschedule,
        originalBooking: originalBooking,
        rescheduleFee: rescheduleFee,
        rescheduleRule: rescheduleRule
      }
    });
  };

  const calculateTotal = () => {
    // If in reschedule mode and there's a reschedule fee, return only the reschedule fee
    if (isReschedule && rescheduleFee > 0) {
      return rescheduleFee;
    }
    
    // Otherwise, calculate normal total
    return cartItems.reduce((total, item) => {
      // Parse price properly - handle both string and number formats
      let price = item.price || 0;
      
      if (typeof price === 'string') {
        // Remove $ and any commas, then parse as float
        price = parseFloat(price.replace(/[$,]/g, '')) || 0;
      } else {
        // If it's already a number, use it directly
        price = parseFloat(price) || 0;
      }
      
      return total + price;
    }, 0);
  };

  const getTotalDuration = () => {
    const totalDuration = cartItems.reduce((total, item) => {
      let duration = item.duration || 60;
      // Ensure duration is a number
      if (typeof duration === 'string') {
        // Remove any non-numeric characters and parse
        duration = parseInt(duration.replace(/[^0-9]/g, '')) || 60;
      } else {
        duration = parseInt(duration) || 60;
      }
      
      // Debug: Log each item's duration
      console.log('🔍 [DURATION] Item duration calculation:', {
        itemName: item.name || item.serviceName,
        originalDuration: item.duration,
        parsedDuration: duration,
        runningTotal: total + duration
      });
      
      return total + duration;
    }, 0);
    
    // Debug: Log final total duration
    console.log('🔍 [DURATION] Final total duration:', {
      cartItems: cartItems,
      totalDuration: totalDuration
    });
    
    return totalDuration;
  };

  // Helper function to format duration properly
  const formatDuration = (duration) => {
    if (!duration) return '60 min';
    
    // If duration already contains 'min', return as is
    if (typeof duration === 'string' && duration.toLowerCase().includes('min')) {
      return duration;
    }
    
    // If it's a number, add 'min'
    const numDuration = parseInt(duration) || 60;
    return `${numDuration} min`;
  };

  // Helper function to format price properly
  const formatPrice = (price) => {
    if (!price) return '$0.00';
    
    // If price is already a string with $, return as is
    if (typeof price === 'string' && price.includes('$')) {
      return price;
    }
    
    // Convert to number and format
    const numPrice = typeof price === 'string' 
      ? parseFloat(price.replace(/[$,]/g, '')) 
      : parseFloat(price) || 0;
    
    return `$${numPrice.toFixed(2)}`;
  };

  const isSlotSelected = (slot) => {
    const isSelected = selectedSlots.some(selectedSlot => selectedSlot.startTime === slot.startTime);
    
    // Debug slot selection
    if (isSelected) {
      console.log('🟢 Slot is selected:', slot.startTime, 'Selected slots:', selectedSlots.map(s => s.startTime));
    }
    
    return isSelected;
  };

  const isSlotStartTime = (slot) => {
    return selectedTime === slot.startTime;
  };

  if (loading || !provider) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <LinearProgress sx={{ width: '50%', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Loading availability for provider...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fetching available time slots
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5' }}>
      <Box sx={{ 
        p: 3,
        // Add bottom padding for mobile to ensure content is visible above bottom navigation
        pb: isMobile ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : 3
      }}>

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3,
        backgroundColor: '#1a237e',
        color: 'white',
        p: isReschedule ? 1 : 1.5,
        borderRadius: 1,
        boxShadow: 2
      }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2, color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant={isReschedule ? "h6" : "h5"} sx={{ fontWeight: 'bold', color: 'white' }}>
          {isReschedule ? 'Reschedule Booking' : 'Select Date & Time'}
        </Typography>
            </Box>


      {/* Provider Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {provider?.name || 'Provider'}
                </Typography>
              <Typography variant="body2" color="text.secondary">
                {cartItems.length} services • {(() => {
                  const totalDuration = getTotalDuration();
                  const hours = Math.floor(totalDuration / 60);
                  const minutes = totalDuration % 60;
                  if (hours > 0) {
                    return `${hours}h ${minutes}m`;
                  } else {
                    return `${minutes}m`;
                  }
                })()}
                {isReschedule && rescheduleFee > 0 && (
                  <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                    {' • Reschedule Fee: $' + rescheduleFee.toFixed(2)}
                  </span>
                )}
              </Typography>
              
              {/* Show individual services */}
              {cartItems.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {cartItems.map((item, index) => (
                    <Typography key={index} variant="caption" display="block" sx={{ color: '#666', fontSize: '0.75rem' }}>
                      • {item.name || item.serviceName} ({formatDuration(item.duration)})
                    </Typography>
                  ))}
                </Box>
              )}
              
            </Box>
          </Box>
        </CardContent>
      </Card>


       

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Date Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
                             <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                 Select Date
               </Typography>
                               <Box 
                  onClick={(e) => {
                    // Open the date picker when clicking anywhere on the field
                    const input = e.currentTarget.querySelector('input[type="date"]');
                    if (input) {
                      input.showPicker();
                    }
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TextField
                    type="date"
                    fullWidth
                    variant="outlined"
                    value={selectedDate}
                    onChange={(e) => handleDateSelect(e.target.value ? new Date(e.target.value) : null)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: new Date().toISOString().split('T')[0]
                    }}
                    sx={{ 
                      mb: 2,
                      '& .MuiInputBase-root': {
                        cursor: 'pointer'
                      },
                      '& .MuiInputBase-input': {
                        cursor: 'pointer'
                      }
                    }}
                  />
                </Box>
            </CardContent>
          </Card>

          {/* Time Selection */}
          {selectedDate && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Select Time
                </Typography>
                                     {selectedSlots.length > 0 && (
                     <Chip
                       icon={<AccessTimeIcon />}
                       label={`${selectedSlots[0].startTime} - ${minutesToTime(timeToMinutes(selectedSlots[selectedSlots.length - 1].startTime) + 10)}`}
                       color="success"
                       variant="filled"
                       sx={{ 
                         backgroundColor: '#4CAF50',
                         color: 'white',
                         fontWeight: 'bold',
                         display: 'none'  // Hidden as requested
                       }}
                     />
                   )}
                </Box>
                {/* Display no slots message if available */}
                {noSlotsMessage && !removingServices && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Alert 
                      severity={noSlotsMessage.toLowerCase().includes('closed') ? 'info' : 'warning'} 
                      sx={{ 
                        mb: 2,
                        ...(noSlotsMessage.toLowerCase().includes('closed') && {
                          backgroundColor: '#e3f2fd',
                          '& .MuiAlert-icon': {
                            color: '#1976d2'
                          }
                        })
                      }}
                    >
                      {noSlotsMessage}
                      {/* Show helpful message for today's date */}
                      {selectedDate === new Date().toISOString().split('T')[0] && 
                       !noSlotsMessage.toLowerCase().includes('closed') && (
                        <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                          Please choose another date to see available slots.
                        </Box>
                      )}
                    </Alert>
                    {unavailableServices.length > 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          console.log('🔍 [BUTTON] Remove services button clicked!');
                          handleRemoveUnavailableServices();
                        }}
                        disabled={removingServices}
                        sx={{ mt: 1 }}
                        startIcon={removingServices ? <CircularProgress size={16} /> : null}
                      >
                        {removingServices 
                          ? 'Removing Services...' 
                          : `Remove ${unavailableServices.join(', ')} & Try Again`
                        }
                      </Button>
                    )}
                  </Box>
                )}

                {/* Show loading state when removing services */}
                {removingServices && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <LinearProgress sx={{ mb: 2, height: 6, borderRadius: 3 }} />
                    <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
                      Loading availability for provider...
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Fetching available time slots
                    </Typography>
                  </Box>
                )}


                {/* Partial loading indicator for date changes */}
                {partialLoading && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <LinearProgress sx={{ mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading availability for selected date...
                    </Typography>
                  </Box>
                )}
                
                <Grid container spacing={1}>
                   {(() => {
                     console.log('🔍 [SLOT RENDER] Looking for date:', selectedDate, 'in availableSlots:', availableSlots);
                     const daySlots = availableSlots.find(day => day.date === selectedDate);
                     console.log('🔍 [SLOT RENDER] Found daySlots:', daySlots);
                     if (!daySlots || !daySlots.slots) {
                       console.log('❌ No daySlots found for date:', selectedDate, 'availableSlots:', availableSlots);
                       return null;
                     }
                     
                     // For now, return all available slots from the backend
                     // The backend already handles team member availability and sequential scheduling
                     console.log('🔍 [SLOT RENDER] Rendering slots for date:', selectedDate, 'slots count:', daySlots.slots.length);
                     console.log('🔍 [SLOT RENDER] First slot structure:', daySlots.slots[0]);
                     return daySlots.slots.map((slot, index) => {
                       console.log('🔍 [SLOT RENDER] Rendering slot:', index, slot.startTime);
                       return (
                      <Grid item xs={4} sm={3} md={2} lg={2} key={`${slot.startTime}-${index}`}>
                        <Button
                          variant="outlined"
                          color="inherit"
                          disabled={!slot.available || slotSelecting}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Show alert to confirm click is happening
                            
                            console.log('🔍 [BUTTON CLICK] ===== BUTTON CLICKED =====');
                            console.log('🔍 [BUTTON CLICK] Slot button clicked:', slot.startTime);
                            console.log('🔍 [BUTTON CLICK] Event:', e);
                            console.log('🔍 [BUTTON CLICK] Slot object:', slot);
                            console.log('🔍 [BUTTON CLICK] Slot available:', slot.available);
                            console.log('🔍 [BUTTON CLICK] Slot selecting:', slotSelecting);
                            console.log('🔍 [BUTTON CLICK] Button click at:', new Date().toISOString());
                            
                            // Check if slot is available before proceeding
                            if (!slot.available) {
                              console.log('❌ [BUTTON CLICK] Slot is not available, ignoring click');
                              return;
                            }
                            
                            // Check if already selecting a slot
                            if (slotSelecting) {
                              console.log('⏳ [BUTTON CLICK] Already selecting a slot, ignoring click');
                              return;
                            }
                            
                            try {
                              await handleTimeSelect(slot.startTime, [slot], null);
                            } catch (error) {
                              console.error('❌ [BUTTON CLICK] Error in handleTimeSelect:', error);
                            }
                          }}
                          sx={{
                             width: '100%',
                             py: 1,
                             px: 0.5,
                             backgroundColor: isSlotSelected(slot) ? '#4CAF50' : (slotSelecting ? '#f0f0f0' : 'white'),
                             color: isSlotSelected(slot) ? 'white' : (slotSelecting ? '#999' : '#333333'),
                             border: isSlotSelected(slot) ? '3px solid #2E7D32' : '1px solid #ddd',
                             borderRadius: '6px',
                             fontWeight: isSlotSelected(slot) ? 'bold' : 'normal',
                             boxShadow: isSlotSelected(slot) ? '0 2px 8px rgba(76, 175, 80, 0.3)' : 'none',
                             transform: isSlotSelected(slot) ? 'scale(1.02)' : 'scale(1)',
                             transition: 'all 0.2s ease-in-out',
                             opacity: slotSelecting ? 0.7 : 1,
                             cursor: slotSelecting ? 'not-allowed' : 'pointer',
                             '&:hover': {
                               backgroundColor: isSlotSelected(slot) ? '#388E3C' : (slotSelecting ? '#f0f0f0' : '#f8f8f8'),
                               borderColor: isSlotSelected(slot) ? '#2E7D32' : '#bbb',
                               transform: isSlotSelected(slot) ? 'scale(1.02)' : (slotSelecting ? 'scale(1)' : 'scale(1.01)')
                             }
                           }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                            {(() => {
                              console.log('🔍 [SLOT DISPLAY] Slot startTime:', slot.startTime, 'Type:', typeof slot.startTime);
                              return slot.startTime ? formatTimeToAMPM(slot.startTime) : 'Invalid Time';
                            })()}
                          </Typography>
                        </Button>
                      </Grid>
                     );
                     });
                   })()}
                </Grid>


                {/* Selected Slot Schedule Display */}
                {selectedTime && selectedSlots.length > 0 && (
                  <Card sx={{ mt: 3, backgroundColor: '#f8f9fa' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2E7D32' }}>
                        📅 Selected Appointment Schedule
                      </Typography>
                      
                      {selectedSlots[0].schedule && selectedSlots[0].schedule.services && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Service Schedule:
                          </Typography>
                          {selectedSlots[0].schedule.services.map((service, index) => (
                            <Box key={index} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 1,
                              mb: 1,
                              backgroundColor: 'white',
                              borderRadius: 1,
                              border: '1px solid #e0e0e0'
                            }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {service.serviceName} - {service.startTime} to {service.endTime}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Cart Summary */}
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Order Summary
              </Typography>
              
              <List dense>
                {/* Always show service details first */}
                {cartItems.map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText
                      primary={item.serviceName || item.name}
                      secondary={formatDuration(item.duration)}
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatPrice(item.price)}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                
                {/* Show reschedule fee if applicable */}
                {isReschedule && rescheduleFee > 0 && (
                  <ListItem sx={{ px: 0, borderTop: '1px solid #e0e0e0', mt: 1, pt: 1 }}>
                    <ListItemText
                      primary="Reschedule Fee"
                      secondary="Service rescheduling charge"
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                        {formatPrice(rescheduleFee)}
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatPrice(calculateTotal())}
                </Typography>
              </Box>

              {selectedSlots.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Selected Time:
                  </Typography>
                  <Chip
                    icon={<CalendarTodayIcon />}
                    label={`${new Date(selectedDate).toLocaleDateString()} at ${formatTimeToAMPM(selectedTime)}`}
                    color="primary"
                    variant="outlined"
                  />

                </Box>
              )}

              {/* Timer Display */}
              {selectedSlots.length > 0 && timerState.isVisible && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Alert 
                    severity="info" 
                    icon={<AccessTimeIcon />}
                    sx={{ 
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #2196f3',
                      '& .MuiAlert-message': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Slots Reserved
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedSlots.length} consecutive slots selected
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {Math.floor(timerState.seconds / 60)}:{(timerState.seconds % 60).toString().padStart(2, '0')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Time remaining
                      </Typography>
                    </Box>
                  </Alert>
                </Box>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={!selectedTime || selectedSlots.length === 0}
                onClick={handleContinueToCheckout}
                startIcon={<ShoppingCartIcon />}
                sx={{ 
                  mt: 2,
                  mb: isMobile ? 3 : 0,
                  py: isMobile ? 1.5 : 1
                }}
              >
                {!selectedTime || selectedSlots.length === 0
                  ? 'Select Time Slot to Continue' 
                  : 'Continue to Checkout'}
              </Button>

              
              

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      



      {/* Enhanced Location Dialog */}
      <EnhancedLocationDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        onLocationSelect={(locationData) => {
          setServiceAddress(locationData.address);
          setLocationError('');
          
          // Save location data to localStorage
          const locationInfo = {
            address: locationData.address,
            fullAddress: locationData.address,
            latitude: locationData.lat,
            longitude: locationData.lng,
            city: locationData.address ? locationData.address.split(',')[0] : '', // Extract city from address with null check
            type: locationData.type
          };
          localStorage.setItem('userLocation', JSON.stringify(locationInfo));
          
          setLocationDialogOpen(false);
        }}
      />



      {/* Timer is now handled by GlobalTimer component */}
      </Box>
      
      {/* Bottom Navigation */}
      <BottomNavigation />
      
      {/* Snackbar for user notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ 
          bottom: { xs: 70, sm: 16 }, // Position above bottom navigation (56px nav + 14px spacing)
          zIndex: 9999 // Ensure it's above everything including bottom nav
        }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Availability; 